import axios, { AxiosError } from 'axios';
import { Collection, Feature, Overlay } from 'ol';
import { always, click, never, platformModifierKeyOnly, primaryAction } from 'ol/events/condition';
import Circle from 'ol/geom/Circle';
import Geometry from 'ol/geom/Geometry';
import GeometryType from 'ol/geom/GeometryType';
import Polygon, { fromCircle } from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { Draw, Interaction, Modify, Select, Snap, Translate } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { Map as OlMap } from 'ol';
import 'ol/ol.css';
import { Vector as VectorSource } from 'ol/source';
import { Style } from 'ol/style';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { layerToJson, gpsToMap } from '../LiveAndReplay/TypeConversions';
import {
  tripwiresStyle,
  interactionStyle,
  updateGeofencesStyle,
  getGeofenceStyleFromZone,
  clearedFenceDrawStartStyle,
  trackingFenceDrawStartStyle,
} from '../Styles/GeofenceStyles';
import { getMicrofencesVectorLayer, getVectorLayer, getVectorLayers } from '../../../API/layers';
import { useAtomValue, useSetAtom } from 'jotai';
import { CID, PID } from '../../../store/user';
import { ACCESS_JWT_TOKEN, AUTHED_REQUEST_CONFIG } from '../../../store/auth';
import { TRIGGERS_URL } from '../../../store/url';
import GeoJSON, { GeoJSONFeature } from 'ol/format/GeoJSON';
import Point from 'ol/geom/Point';
import { containsExtent, Extent, getCenter } from 'ol/extent';
import { transform, transformExtent } from 'ol/proj';
import { debounce } from '../../../util/debounce';
import LineString from 'ol/geom/LineString';
import BaseVectorLayer from 'ol/layer/BaseVector';
import CanvasVectorLayerRenderer from 'ol/renderer/canvas/VectorLayer';
import CanvasVectorTileLayerRenderer from 'ol/renderer/canvas/VectorTileLayer';
import CanvasVectorImageLayerRenderer from 'ol/renderer/canvas/VectorImageLayer';
import WebGLPointsLayerRenderer from 'ol/renderer/webgl/PointsLayer';
import { ModifyEvent } from 'ol/interaction/Modify';
import { SaveResult, SAVE_NOTIFICATION } from '../../../store/notifications';
import { MAP_API_KEYS } from '../../../store/map';
import { GridRowData } from '@material-ui/data-grid';
import {
  Bounds,
  FenceNameIdZone,
  GeofenceFilter,
  GeomobyOverride,
  MapSourceType,
  MicrofenceAssetId,
  NameId,
  ReassignedFence,
  SearchType,
} from '../types';
import { normaliseErrorMessage } from '../../../util/ErrorMessages';
import {
  BufferShapeType,
  DrawType,
  EditType,
  EntityType,
  FenceEntityType,
  FenceZone,
  GeofenceEntityType,
  GeoJsonType,
  Interactions,
  MapType,
  MeasurementType,
  MicrofenceEntity,
  MicrofenceZone,
  SearchTypeIDs,
  SearchTypeValue,
} from '../../../util/enums';
import { geometryTypeOfEntity } from '../commons';
import {
  addDrawInteraction,
  addModifyInteraction,
  calculateArea,
  calculateMeasurementDistance,
  clearAllInteractions,
  defaultScaleRotFenceStyle,
  polygonsContainedWithinPolygon,
  featureContainsPoint,
  isPointInsideAnyMultiPolygon,
  mergeMultiPolygons,
  removeInteractions,
  removePolygonFromMultiPolygon,
} from './Draw';
import {
  foundFeatureWithDuplicateName,
  foundFeatureWithDuplicateOverride,
  foundFeatureWithEmptyGeomobyPropertyValue,
  foundFeatureWithIncompleteOverride,
  foundFeatureWithInvalidPropertyOrOverride,
  foundFeatureWithUndefinedName,
  featureHasInvalidBreachBufferGeometry,
  foundFeatureWithInvalidBreachGeometry,
} from './Validator';
import { jsUcFirst } from '../../Global/StringFormatterFunctions';
import {
  ALL_LAYERS,
  FRESH,
  UNKNOWN_LAYER,
  initialExtentInDegrees,
  initialLatitude,
  initialLongitude,
  initialZoomHeight,
  TRACKING_BOUNDS,
  FRESH_LAYER,
  ZOOM_THRESHOLD,
  MICROFENCE_LAYER_ID,
  MICROFENCE_LAYER_LABEL,
  BOUNDARY_RSSI_DEFAULT,
  TIMEOUT_SECONDS_DEFAULT,
  IS_INVISIBLE,
  GEOFENCE_LAYER,
  SCALABLE_ROTATABLE_LAYER,
  PIN_LAYER,
  UPDATED,
  GEOMETRY_HAS_CHANGED,
  STREET_ADDRESS_ZOOM_LEVEL,
} from '../../../util/constants';
import {
  animateToLocation,
  applyGeofenceFilterToLayerOnMap,
  createFeatureFromTypeAndCoordinates,
  deselectFences,
  dropLocationMarker,
  getLayerFromMap,
  getSmallestAreaFeature,
  transformMultipolygon,
} from '../Helpers';
import { CLEARED_ZONE, WHITE } from '../../../Style/GeoMobyBaseTheme';
import { EditState, MapState, EditorMapProps } from './Props';
import { createMap, createMapDefaults } from '../InitMap';
import { measurementToolOff, measurementToolPonter } from '../Styles/MiscStyles';
import API from '../../../API/api';
import { LocationDisplayType, LocationSearchData } from '../Toolbar/Location/LocationSearch';
import { PROMISE_VOID, VOID } from '../../../util/typeUtils';
import { DialogType } from './enums';
import { Microfence, PagiantedMicrofencesQuery } from '../../../API/types/triggers';
import { geofenceToMulipartFormData } from '../../../util/geofenceUtils';

type AnyRenderer =
  | CanvasVectorLayerRenderer
  | CanvasVectorTileLayerRenderer
  | CanvasVectorImageLayerRenderer
  | WebGLPointsLayerRenderer;
type HitDetectionLayer = BaseVectorLayer<VectorSource<Geometry>, AnyRenderer>;

const styleCache = new Map<string, Style[]>();

export const EditorMapContext = createContext<EditorMapProps>({
  animateToFeature: PROMISE_VOID,
  availableGeofences: [],
  availableMicrofences: [],
  changeVisibility: VOID,
  clearFilter: false,
  setClearFilter: VOID,
  count: 0,
  createNewLayer: VOID,
  currentCenter: undefined,
  deletedFenceIdsRef: { current: [] },
  deleteLayer: PROMISE_VOID,
  deselectFence: false,
  editMode: undefined,
  findFeature: async () => void 0,
  fencesLoading: false,
  freshFences: [],
  geofenceFilter: undefined,
  hasFences: false,
  isLoading: false,
  knownExtentsRef: { current: [] },
  layerIds: [],
  layersHaveChanged: false,
  locationDisplay: undefined,
  mapIsLoading: false,
  mapState: undefined,
  mapSourceType: 'Terrain & Roads',
  microfenceFilter: undefined,
  microfences: [],
  moveUnknownFenceToExistingLayer: VOID,
  openDialog: undefined,
  openGenericDialog: false,
  reassignedFences: [],
  refreshSearch: false,
  removeBufferZone: PROMISE_VOID,
  resetLayerChanges: PROMISE_VOID,
  saveLayerChanges: PROMISE_VOID,
  searchType: undefined,
  selectedFromMap: false,
  selectedGeofence: undefined,
  selectedLayer: undefined,
  selectedMicrofence: undefined,
  setAsBreachZone: PROMISE_VOID,
  setAsBufferZone: PROMISE_VOID,
  setAvailableGeofences: VOID,
  setAvailableMicrofences: VOID,
  setCount: VOID,
  setDeselectFence: VOID,
  setDrawType: VOID,
  setEditMode: VOID,
  setFreshFences: VOID,
  setGeofenceFilter: VOID,
  setIsLoading: VOID,
  setLayerIds: VOID,
  setLayersHaveChanged: VOID,
  setLocationDisplay: VOID,
  setLocationSearchData: VOID,
  setMapSourceType: VOID,
  setMicrofenceFilter: VOID,
  setOpenDialog: VOID,
  setOpenGenericDialog: VOID,
  setReassignedFences: VOID,
  setRefreshSearch: VOID,
  setRenamingLayer: VOID,
  setSearchType: VOID,
  setSelectedFromMap: VOID,
  setSelectedGeofence: VOID,
  setSelectedLayer: VOID,
  setSelectedMicrofence: VOID,
  setSourceRef: { current: undefined },
  setShowFilter: VOID,
  setShowGhostGeofences: VOID,
  setZone: PROMISE_VOID,
  showFilter: false,
  showGhostGeofences: false,
  stopEditingFeature: VOID,
  unsetAsBreachZone: PROMISE_VOID,
  unsetZone: PROMISE_VOID,
  updateFenceIdentifiers: PROMISE_VOID,
  updateGeomobyOverrides: PROMISE_VOID,
  updateGeomobyProperties: PROMISE_VOID,
  deleteFeature: PROMISE_VOID,
  editFeature: VOID,
  drawType: undefined,
  editType: undefined,
  setEditType: VOID,
  measurementType: undefined,
  changeMeasurementType: VOID,
  findBufferFromBreach: VOID,
});

/* Editor Map */
export const EditorMapProvider = ({ children }: { children: ReactNode }) => {
  const cid = useAtomValue(CID);
  const pid = useAtomValue(PID);
  const authedRequestConfig = useAtomValue(AUTHED_REQUEST_CONFIG);
  const accessJwtToken = useAtomValue(ACCESS_JWT_TOKEN);
  const Authorization = `Bearer ${accessJwtToken}`;
  const setSaveNotification = useSetAtom(SAVE_NOTIFICATION);
  const triggersUrl = useAtomValue(TRIGGERS_URL);
  const mapApiKeys = useAtomValue(MAP_API_KEYS);

  const deletedFenceIdsRef = useRef<{ id: string; type: FenceEntityType }[]>([]);
  const editRef = useRef<EditState | undefined>();
  const editTypeRef = useRef<EditType | undefined>();
  const initRef = useRef<boolean>(false);
  const knownExtentsRef = useRef<Extent[]>([]);
  const layersHaveChangedRef = useRef<boolean>(false);
  const measureTooltipRef = useRef<Overlay>();
  const measureTooltipElementRef = useRef<HTMLElement>();
  const freshFencesRef = useRef<GridRowData[]>([]);
  const setSourceRef = useRef<(type: MapSourceType) => void>();
  const totalFenceCountRef = useRef<number>(0);

  const [availableGeofences, setAvailableGeofences] = useState<GridRowData[]>([]);
  const [availableMicrofences, setAvailableMicrofences] = useState<GridRowData[]>([]);
  const [bounds, setBounds] = useState<Bounds>({
    latitude: initialLatitude,
    longitude: initialLongitude,
    extentInDegrees: initialExtentInDegrees,
  });
  const [clearFilter, setClearFilter] = useState<boolean>(false);
  const [currentCenter, setCurrentCenter] = useState<number[] | undefined>();
  const [deselectFence, setDeselectFence] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<
    | {
        type: DialogType;
        title: string;
      }
    | undefined
  >();
  const [drawType, setDrawType] = useState<DrawType | undefined>();
  const [editType, setEditType] = useState<EditType | undefined>();
  const [editMode, setEditMode] = useState<EntityType | undefined>();
  const [fencesLoading, setFencesLoading] = useState<boolean>(true);
  const [geofenceFilter, setGeofenceFilter] = useState<GeofenceFilter | undefined>();
  const [hasFences, setHasFences] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [layersHaveChanged, setLayersHaveChanged] = useState<boolean>(false);
  const [layerIds, setLayerIds] = useState<NameId[]>([]);
  const [locationDisplay, setLocationDisplay] = useState<LocationDisplayType>();
  const [locationSearchData, setLocationSearchData] = useState<LocationSearchData | undefined>();
  const [mapIsLoading, setMapIsLoading] = useState<boolean>(true);
  const [mapState, setMapState] = useState<MapState | undefined>();
  const [mapSourceType, setMapSourceType] = useState<MapSourceType>('Terrain & Roads');
  const [measurementType, setMeasurementType] = useState<MeasurementType | undefined>();
  const [microfences, setMicrofences] = useState<Microfence[]>([]);
  const [microfenceFilter, setMicrofenceFilter] = useState<PagiantedMicrofencesQuery | undefined>();
  const [freshFences, setFreshFences] = useState<GridRowData[]>([]);
  const [openGenericDialog, setOpenGenericDialog] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [reassignedFences, setReassignedFences] = useState<ReassignedFence[]>([]);
  const [renamingLayer, setRenamingLayer] = useState<string | null>(null);
  const [refreshSearch, setRefreshSearch] = useState<boolean>(false);
  const [selectedFromMap, setSelectedFromMap] = useState<boolean>(false);
  const [selectedGeofence, setSelectedGeofence] = useState<GridRowData | undefined>();
  const [selectedMicrofence, setSelectedMicrofence] = useState<GridRowData | undefined>();
  const [selectedLayer, setSelectedLayer] = useState<NameId | undefined>();
  const [searchType, setSearchType] = useState<SearchType | undefined>();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [showGhostGeofences, setShowGhostGeofences] = useState<boolean>(false);
  const [specifiedCoordinates, setSpecifiedCoordinates] = useState<[number, number]>();
  const [zoneChange, setZoneChange] = useState<Date | null>(null);

  if (!specifiedCoordinates) {
    navigator.geolocation?.getCurrentPosition(
      loc => {
        setSpecifiedCoordinates([loc?.coords.longitude, loc?.coords.latitude]);
      },
      () => {
        setSpecifiedCoordinates([initialLongitude, initialLatitude]);
      },
    );
  }

  const debouncedOnMapMoved = useRef(
    debounce(
      (olmap: OlMap) => {
        const view = olmap?.getView();
        if (!view) return;
        const viewCenter = view.getCenter();
        if (!viewCenter) return;
        const center = transform(viewCenter, view.getProjection(), 'EPSG:4326');
        const zoom = view.getZoom() || 0;
        const res = view.getResolution() || 0;
        if (isNaN(center[0]) || isNaN(center[1])) return;
        const newBounds = {
          latitude: center[1],
          longitude: center[0],
          extentInDegrees:
            (zoom <= initialZoomHeight
              ? initialExtentInDegrees
              : (res / Math.min(zoom, ZOOM_THRESHOLD)) * 0.1) ?? initialExtentInDegrees,
        };
        setBounds(newBounds);
        updateMap(olmap, newBounds);
      },
      1000,
      { leading: true },
    ),
  ).current;

  const animateToFeature = useCallback(
    async (feature: GridRowData) => {
      if (!mapState) return;
      if (feature.layerId === MICROFENCE_LAYER_ID || feature?.id?.includes(FRESH)) {
        const coordinates = feature.point?.coordinates;
        if (!coordinates) {
          console.warn('No coordinates available for:', feature);
          return;
        }
        animateToLocation(
          mapState.map?.getView(),
          transform([...coordinates, ...coordinates], 'EPSG:4326', 'EPSG:3857') as [
            number,
            number,
            number,
            number,
          ],
        );
        return;
      }

      if (
        deletedFenceIdsRef.current.find(d => d.id === feature.id) ||
        feature.layerId === MICROFENCE_LAYER_ID
      )
        return;

      const geofence = (await API.triggers.getGeofence({
        id: feature?.id,
        layerId: feature?.layerId,
        type: geometryTypeOfEntity(feature) as unknown as GeofenceEntityType,
        projectId: pid,
        clientId: cid,
        Authorization: `Bearer ${accessJwtToken}`,
      })) as GridRowData | undefined;

      if (!geofence) return;
      const newFence = createFeatureFromTypeAndCoordinates(geofence);
      const extent = transformExtent(newFence.getExtent(), 'EPSG:4326', 'EPSG:3857');
      if (!extent) return;
      animateToLocation(mapState.map?.getView(), extent as [number, number, number, number]);
    },
    [mapState, cid, pid, accessJwtToken],
  );

  const findFeature = useCallback(
    async (olmap: OlMap, fenceId: string, layerId: string, type: FenceEntityType | undefined) => {
      let feature;

      const layer = getLayerFromMap(olmap, layerId);
      if (!layer) return;

      if (layerId === MICROFENCE_LAYER_ID) {
        return getLayerFromMap(olmap, MICROFENCE_LAYER_ID)
          ?.getSource()
          ?.getFeatures()
          ?.find((f: Feature<Geometry>) => f.get('id') === fenceId);
      }

      feature = layer
        ?.getSource()
        .getFeatures()
        .find(f => f.get('id') === fenceId);
      if (!feature) {
        const geofence = (await API.triggers.getGeofence({
          id: fenceId,
          layerId: layerId,
          type: type as unknown as GeofenceEntityType,
          projectId: pid,
          clientId: cid,
          Authorization: `Bearer ${accessJwtToken}`,
        })) as GridRowData | undefined;

        if (geofence) {
          const layerName = layerIds.find(l => l.id === layerId)?.name;
          const newFence = createFeatureFromTypeAndCoordinates(geofence);
          if (type === FenceEntityType.Polygon) {
            const coords: number[][] = (newFence as Polygon).getCoordinates()[0];
            (newFence as Polygon).setCoordinates([
              coords.map(coord => transform(coord, 'EPSG:4326', 'EPSG:3857')),
            ]);
          } else if (type === FenceEntityType.Multipolygon) {
            const coords: number[][][][] = (newFence as MultiPolygon)
              .getCoordinates()
              .map(polygons => {
                return polygons.map(polygon =>
                  polygon.map(coord => transform(coord, 'EPSG:4326', 'EPSG:3857')),
                );
              });
            (geofence as GridRowData).zone = FenceZone.cleared;
            (newFence as MultiPolygon).setCoordinates(coords);
          } else if (type === FenceEntityType.Line) {
            const coords: number[][] = (newFence as LineString).getCoordinates();
            (newFence as LineString).setCoordinates(
              coords.map(coord => transform(coord, 'EPSG:4326', 'EPSG:3857')),
            );
          }
          feature = new Feature(newFence);
          feature.setGeometry(newFence);
          feature.setProperties({ ...geofence, layerId, layerName });
          // Check again!
          const existingFeature = layer
            ?.getSource()
            .getFeatures()
            .find(f => f.get('id') === fenceId);
          if (existingFeature) {
            layer?.getSource().removeFeature(existingFeature);
          }
          layer?.getSource().addFeature(feature);
          if (layerName === TRACKING_BOUNDS && geofence.type === GeofenceEntityType.Polygon) {
            feature.setStyle(
              getGeofenceStyleFromZone({ zone: geofence.zone, type, layerName, selected: true }),
            );
          }
          return feature;
        }
      }
      return feature;
    },
    [accessJwtToken, cid, layerIds, pid],
  );

  const unsetMapClickSelection = (olmap: OlMap) => {
    olmap.getInteractions().forEach(i => i instanceof Select && olmap.removeInteraction(i));
  };

  const setMapMoveHandler = useCallback(
    (olmap: OlMap) => {
      const onEvent = () => {
        setCurrentCenter(olmap.getView().getCenter());
        const zoom = olmap.getView().getZoom() || 0;
        if (zoom > ZOOM_THRESHOLD) {
          olmap.getView().setZoom(ZOOM_THRESHOLD);
        }
        debouncedOnMapMoved(olmap);
      };
      olmap.on('moveend', onEvent);
      olmap.getView().on('change:resolution', onEvent);
    },
    [debouncedOnMapMoved],
  );

  const layerChanged = useCallback(
    (olmap: OlMap) => {
      if (!olmap) return;
      if (
        !(selectedGeofence ?? selectedMicrofence) &&
        olmap
          .getAllLayers()
          .find(
            lyr => lyr.get('id') === UNKNOWN_LAYER || lyr.getSource()?.get('id') === UNKNOWN_LAYER,
          )
      )
        return;
      setLayersHaveChanged(true);
    },
    [selectedGeofence, selectedMicrofence],
  );

  const featureModified = useCallback(
    (olmap: OlMap, event: ModifyEvent, source: VectorSource<Geometry>) => {
      const modifiedFids = event.features.getArray();
      source.forEachFeature(feature => {
        const modifiedFeature = modifiedFids.find(f => f.get('id')?.includes(feature.get('id')));
        deselectFences(olmap, modifiedFids?.[0].get('id'));
        if (modifiedFeature) {
          feature.setProperties({ updated: true, geometryHasChanged: true });

          if (modifiedFeature && modifiedFeature.get('layerId') !== MICROFENCE_LAYER_ID) {
            feature.setGeometry((modifiedFeature as Feature<Geometry>).getGeometry());
            const geometry = modifiedFeature?.getGeometry();

            const type = geometryTypeOfEntity(feature.getProperties());
            if (geometry) {
              if (type === FenceEntityType.Polygon) {
                const coordinates = (geometry as Polygon).getCoordinates()[0];
                const newCoords = coordinates.map(coord =>
                  transform(coord, 'EPSG:3857', 'EPSG:4326'),
                );
                if (newCoords && feature.get('points')) {
                  feature.get('points').coordinates = [newCoords];
                }
              } else if (type === FenceEntityType.Multipolygon) {
                const coordinates = (geometry as MultiPolygon).getCoordinates()[0][0];
                const newCoords = coordinates.map(coord =>
                  transform(coord, 'EPSG:3857', 'EPSG:4326'),
                );
                if (newCoords && feature.get('points')) {
                  feature.get('points').coordinates = [[newCoords]];
                }
              } else {
                const coordinates = (geometry as LineString).getCoordinates();
                const newCoords = coordinates.map(coord =>
                  transform(coord, 'EPSG:3857', 'EPSG:4326'),
                );
                if (newCoords && feature.get('points')) {
                  feature.get('points').coordinates = newCoords;
                }
              }
            }
          }
        }
      });
      layerChanged(olmap);
    },
    [layerChanged],
  );

  const updateFenceOnMap = (
    layer: VectorLayer<VectorSource<Geometry>>,
    id: string,
    name: string,
    fenceZone: FenceZone | undefined,
    assetId?: MicrofenceAssetId,
    microfenceZone?: MicrofenceZone,
    boundaryRssi?: number,
    timeoutSeconds?: number,
  ) => {
    const feature = layer
      .getSource()
      .getFeatures()
      .find(f => f.get('id') === id);
    if (!feature) return;
    feature.setProperties({ ...feature.getProperties(), name, updated: true });
    if (assetId) {
      feature.set('zone', microfenceZone);
      feature.set('assetId', assetId);
    }
    if (fenceZone) {
      feature.set('zone', fenceZone);
    }
    if (boundaryRssi !== undefined) {
      feature.set('boundaryRssi', boundaryRssi);
    }
    if (timeoutSeconds !== undefined) {
      feature.set('timeoutSeconds', timeoutSeconds);
    }
  };

  const updateFenceIdentifiers = useCallback(
    async (
      olmap: OlMap,
      id: string,
      name: string,
      type: FenceEntityType | undefined,
      layerId: string,
      fenceZone: FenceZone | undefined,
      assetId: MicrofenceAssetId | undefined,
      microfenceZone: MicrofenceZone | undefined,
      boundaryRssi: number | undefined,
      timeoutSeconds: number | undefined,
    ): Promise<void> => {
      if (!id) {
        console.warn(
          'UI should not be able to update a feature name or ID while no feature is selected',
        );
        return;
      }
      const foundLayer = getLayerFromMap(olmap, layerId);
      if (!foundLayer) {
        console.warn(
          'UI should not be able to update a feature name or ID while no layer is selected',
        );
        return;
      }

      if (layerId !== MICROFENCE_LAYER_ID) {
        await findFeature(olmap, id, layerId, type);
      }

      updateFenceOnMap(
        foundLayer,
        id,
        name,
        fenceZone,
        assetId,
        microfenceZone,
        boundaryRssi,
        timeoutSeconds,
      );
      layerChanged(olmap);

      const updatedNameFeature = freshFencesRef.current.find(f => f.id === id);
      if (updatedNameFeature) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== id),
          { ...updatedNameFeature, name },
        ];
      }

      if (layerId !== MICROFENCE_LAYER_ID) {
        const bufferZoneFence = foundLayer
          .getSource()
          .getFeatures()
          .find(f => f.get('parentId') === id && f.get('zone') === FenceZone.buffer);
        const relatedFenceName = bufferZoneFence ? `${name}_warning` : undefined;
        if (relatedFenceName) {
          updateFenceOnMap(
            foundLayer,
            bufferZoneFence?.get('id'),
            relatedFenceName,
            fenceZone === FenceZone.breach ? FenceZone.buffer : undefined,
          );
          layerChanged(olmap);

          const bufferIsFresh = freshFencesRef.current.find(
            f => f.id === bufferZoneFence?.get('id'),
          );
          if (bufferIsFresh) {
            freshFencesRef.current = [
              ...freshFencesRef.current.filter(f => f.id !== bufferZoneFence?.get('id')),
              { ...bufferIsFresh, name: relatedFenceName },
            ];
          }
        }
        setAvailableGeofences(
          availableGeofences.map(fence => {
            if (fence.id === id) {
              return { ...fence, name };
            } else if (relatedFenceName && fence.id === bufferZoneFence?.get('id')) {
              return { ...fence, name: relatedFenceName };
            }
            return fence;
          }),
        );
      } else if (layerId === MICROFENCE_LAYER_ID) {
        setAvailableMicrofences(
          availableMicrofences.map(fence => {
            if (fence.id === id && assetId) {
              return { ...fence, name, assetId };
            }
            return fence;
          }),
        );
      }
      setFreshFences(freshFencesRef.current);
      setLayersHaveChanged(true);
    },
    [availableMicrofences, findFeature, layerChanged, availableGeofences],
  );

  const changeVisibility = useCallback(
    (olmap: OlMap, layerId: string, visible: boolean, opacity?: number) => {
      const foundLayer = getLayerFromMap(olmap, layerId);
      if (!foundLayer) return;

      foundLayer.setVisible(
        layerId === MICROFENCE_LAYER_ID && selectedLayer?.id !== MICROFENCE_LAYER_ID
          ? false
          : visible,
      );
      if (selectedLayer?.id === layerId && visible) {
        applyGeofenceFilterToLayerOnMap(foundLayer, geofenceFilter);
      } else if (!selectedLayer || (selectedLayer?.id === ALL_LAYERS && visible)) {
        applyGeofenceFilterToLayerOnMap(foundLayer, geofenceFilter);
      }
      if (visible && selectedLayer?.id === MICROFENCE_LAYER_ID) {
        foundLayer.setOpacity(layerId === MICROFENCE_LAYER_ID ? 1 : 0.6);
      } else if (visible) {
        foundLayer.setOpacity(1);
      }
      if (opacity) foundLayer.setOpacity(opacity);
      foundLayer.setStyle(updateGeofencesStyle(new Map()));
    },
    [geofenceFilter, selectedLayer],
  );

  const resetLayerChanges = useCallback(
    async (olmap: OlMap): Promise<void> => {
      if (!selectedLayer) return; // UI should not need to reset a layer when no layer is selected;
      if (!olmap) {
        console.warn('UI should not be able to modify a layer while no map exists');
        return;
      }

      deselectFences(olmap);
      if (selectedLayer) {
        changeVisibility(olmap, selectedLayer?.id, false);
      }

      setLayersHaveChanged(false);
      setSelectedGeofence(undefined);
      setSelectedMicrofence(undefined);
      setEditMode(undefined);
      setAvailableGeofences([]);
      setReassignedFences([]);
      freshFencesRef.current = [];
      setFreshFences([]);

      const scaleRotLayer = olmap
        .getAllLayers()
        .find(l => l.getClassName() === SCALABLE_ROTATABLE_LAYER);
      if (scaleRotLayer) {
        olmap.removeLayer(scaleRotLayer);
      }

      setMapIsLoading(true);
      const restoredLayer =
        selectedLayer?.id === MICROFENCE_LAYER_ID
          ? {
              id: MICROFENCE_LAYER_ID,
              name: MICROFENCE_LAYER_LABEL,
              source: await getMicrofencesVectorLayer(triggersUrl, authedRequestConfig, cid, pid),
            }
          : await getVectorLayer(
              triggersUrl,
              authedRequestConfig,
              cid,
              pid,
              selectedLayer?.id,
              bounds,
            );
      setMapIsLoading(false);

      // If no layer with these credentials is returned from db, then remove it from front-end.
      if (!restoredLayer) {
        if (layerIds.length === 0) return;

        const layerToRemove = layerIds.find(i => i.id === selectedLayer?.id)?.id;
        if (layerToRemove) {
          setLayerIds(layerIds.filter(lyr => lyr.id !== layerToRemove));
          olmap.setLayers(
            olmap
              .getAllLayers()
              .filter(
                lyr =>
                  lyr?.getSource().get('id') !== layerToRemove &&
                  lyr.get('id') !== layerToRemove &&
                  lyr?.getSource().get('id') !== FRESH_LAYER &&
                  lyr.get('id') !== FRESH_LAYER,
              ),
          );
        }

        setSelectedLayer(undefined);
        setMapIsLoading(false);
        return;
      }
      restoredLayer.source.setStyle(updateGeofencesStyle(new Map()));

      setTimeout(() => {
        if (!olmap) return;
        const foundLayer = getLayerFromMap(olmap, selectedLayer.id);
        if (foundLayer) {
          olmap.removeLayer(foundLayer);
        }
        olmap.addLayer(restoredLayer.source);
        setLayerIds([
          { id: selectedLayer?.id, name: restoredLayer.name },
          ...layerIds.filter(i => i.id !== selectedLayer?.id),
        ]);
        setSelectedLayer({ id: selectedLayer?.id, name: restoredLayer.name });
        deletedFenceIdsRef.current = [];
        restoredLayer.source.setStyle(updateGeofencesStyle(new Map()));

        // Wait for old layer to be set before refreshing.
        setTimeout(() => {
          setRefreshSearch(true);
          // Force modified/undeleted features to be redrawn
          olmap.getView().adjustZoom(-1);
          olmap.getView().adjustZoom(+1);
          restoredLayer.source.setStyle(updateGeofencesStyle(new Map()));
        });
      });
    },
    [
      changeVisibility,
      layerIds,
      setEditMode,
      setSelectedLayer,
      authedRequestConfig,
      bounds,
      cid,
      pid,
      selectedLayer,
      triggersUrl,
    ],
  );

  const processNextReassignedFence = useCallback(
    (reassignedFence: ReassignedFence, layerId: string) => () => {
      axios
        .patch(
          `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/${reassignedFence.type}/change-layer/${reassignedFence.id}/${reassignedFence.newLayerId}`,
          {},
          authedRequestConfig,
        )
        .then(() => true);
    },
    [cid, pid, triggersUrl, authedRequestConfig],
  );

  const saveMicrofenceLayerChanges = useCallback(
    async (olmap: OlMap): Promise<undefined> => {
      if (!selectedLayer || selectedLayer?.id !== MICROFENCE_LAYER_ID) return;
      const microfenceLayer = getLayerFromMap(olmap, MICROFENCE_LAYER_ID);
      if (!microfenceLayer) return;

      freshFencesRef.current = [];
      setFreshFences([]);
      try {
        await Promise.all(
          deletedFenceIdsRef.current
            .filter(d => !d.id.includes(FRESH))
            .map(({ id }) =>
              axios.delete(`${triggersUrl}/${cid}/${pid}/microfences/${id}`, authedRequestConfig),
            ),
        );
        deletedFenceIdsRef.current = [];

        await Promise.all(
          microfenceLayer
            .getSource()
            ?.getFeatures()
            ?.filter(
              (microfence: Feature<Geometry>) => microfence.get(UPDATED) || microfence.get(FRESH),
            )
            .map(async (microfence: Feature<Geometry>) => {
              if (microfence.getGeometry()?.getType() !== 'Point') {
                console.error(
                  `Should not be trying to save non-point feature for microfence: ${microfence}`,
                );
              }

              const { id, assetId, name, type, zone, boundaryRssi, timeoutSeconds } =
                microfence.getProperties();
              const body = {
                assetId,
                name,
                type,
                zone: zone === MicrofenceZone.none || zone === undefined ? null : zone,
                boundaryRssi,
                timeoutSeconds,
                geometry: {
                  type: 'Point',
                  coordinates: transform(
                    (microfence.getGeometry() as Point)?.getCoordinates(),
                    'EPSG:3857',
                    'EPSG:4326',
                  ),
                },
              };

              if (id?.includes(FRESH)) {
                const newId: string = (
                  await axios.post(
                    `${triggersUrl}/${cid}/${pid}/microfences/`,
                    body,
                    authedRequestConfig,
                  )
                ).data.id;
                return [id, newId];
              } else {
                await axios.patch(
                  `${triggersUrl}/${cid}/${pid}/microfences/${id}`,
                  body,
                  authedRequestConfig,
                );
              }
              return [];
            }),
        );

        await resetLayerChanges(olmap);
        setSelectedLayer({ id: MICROFENCE_LAYER_ID, name: MICROFENCE_LAYER_LABEL });
        setRefreshSearch(true);
        setSaveNotification({ id: SaveResult.SUCCESS, action: 'Save' });
      } catch (error) {
        const errorMessage = normaliseErrorMessage(error as AxiosError, EntityType.Microfence);
        resetLayerChanges(olmap);
        setSaveNotification({
          id: SaveResult.FAIL,
          action: 'Save',
          message: errorMessage,
        });
      }
      deselectFences(olmap);
      olmap.getView().adjustZoom(-1);
      olmap.getView().adjustZoom(+1);
    },
    [
      authedRequestConfig,
      cid,
      pid,
      resetLayerChanges,
      selectedLayer,
      setSaveNotification,
      triggersUrl,
    ],
  );

  const saveGeofenceLayerChanges = useCallback(
    async (olmap: OlMap): Promise<string | undefined> => {
      if (!selectedLayer) return;
      const foundLayer = getLayerFromMap(olmap, selectedLayer.id);
      if (!foundLayer) return;

      let layerId = selectedLayer?.id;
      const layerData = {
        id: selectedLayer.id,
        name: selectedLayer.name,
        source: foundLayer.getSource(),
      };
      const geojson = layerToJson(layerData.source);
      freshFencesRef.current = [];
      setFreshFences([]);

      const features: {
        properties: {
          id: string;
          name: string;
          type: string;
          zone: FenceZone;
          parentId: string;
          geomobyProperties: Record<string, string>;
          geomobyOverrides: GeomobyOverride[];
          geometryHasChanged: boolean;
        };
      }[] = geojson.features
        .filter(
          (feature: GeoJSONFeature) => feature?.properties?.updated || feature?.properties?.fresh,
        )
        .map((feature: GeoJSONFeature) => {
          const type = feature.geometry.type?.toLowerCase()?.includes(FenceEntityType.Line)
            ? FenceEntityType.Line
            : feature.geometry.type?.toLowerCase();
          return {
            ...feature,
            properties: {
              id: feature?.properties?.id,
              name: feature?.properties?.name,
              type,
              geomobyProperties: feature?.properties?.geomobyProperties,
              geomobyOverrides: feature?.properties?.geomobyOverrides,
              zone:
                type === FenceEntityType.Polygon
                  ? feature?.properties?.zone?.toLowerCase() ?? FenceZone.none
                  : undefined,
              parentId: feature?.properties?.parentId,
              geometryHasChanged: feature?.properties.geometryHasChanged,
            },
          };
        });

      const existingFences = features.filter(f => !f.properties.id?.includes(FRESH));
      const newPolygons = features.filter(
        f =>
          f.properties.type?.toLowerCase() === FenceEntityType.Polygon &&
          f.properties.id?.includes(FRESH),
      );

      const newMultipolygons = features.filter(
        f =>
          f.properties.type?.toLowerCase() === FenceEntityType.Multipolygon &&
          f.properties.id?.includes(FRESH),
      );

      const newLines = features.filter(
        f =>
          f.properties.type?.toLowerCase()?.includes(FenceEntityType.Line) &&
          f.properties.id?.includes(FRESH),
      );

      const newlyCreatedPolygons: { id: string; name: string; parentId: string | undefined }[] = [];
      let newlyCreatedMultiPolygons: { id: string; name: string; parentId: string | undefined }[] =
        [];
      let newlyCreatedLines: { id: string; name: string; parentId: string | undefined }[] = [];

      try {
        // Create layer
        if (layerId === FRESH_LAYER) {
          layerId = (
            await axios.post(
              `${triggersUrl}/${cid}/${pid}/geofences`,
              {
                name: layerData.name,
              },
              authedRequestConfig,
            )
          ).data.id;
          const freshLayer = olmap
            ?.getAllLayers()
            .find(
              lyr =>
                lyr instanceof VectorLayer &&
                (lyr.getSource()?.get('id') === FRESH_LAYER || lyr.get('id') === FRESH_LAYER),
            );
          if (freshLayer) {
            freshLayer.set('id', layerId);
            freshLayer
              .getSource()
              .getFeatures()
              .forEach((f: Feature<Geometry>) => {
                f.set('layerId', layerId);
              });
            freshLayer.getSource().setProperties({
              id: layerId,
              name: freshLayer.get('name'),
            });
          }
        } else if (layerId !== FRESH_LAYER && renamingLayer) {
          // Update layer
          layerId = (
            await axios.patch(
              `${triggersUrl}/${cid}/${pid}/geofences/${layerId}`,
              {
                name: renamingLayer,
              },
              authedRequestConfig,
            )
          ).data.id;
        }

        // Delete fences
        if (deletedFenceIdsRef.current.length > 0) {
          await Promise.all(
            deletedFenceIdsRef.current
              .filter(d => !d.id?.includes(FRESH))
              .map(({ type, id }) =>
                axios.delete(
                  `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/${type}/${id}`,
                  authedRequestConfig,
                ),
              ),
          );
          deletedFenceIdsRef.current = [];
        }

        // TODO: Any multipart endpoint calls below will be updated in https://geomoby.atlassian.net/browse/LTP-1559
        // At that time, I will move the calls to the API directory
        // Update existing fences
        if (existingFences.length > 0) {
          await Promise.all(
            existingFences.map(async (feature: GeoJSONFeature) => {
              if (feature?.properties.type !== FenceEntityType.Line) {
                const formData = geofenceToMulipartFormData(
                  feature.geometry,
                  feature.properties ?? {},
                );
                await axios.patch(
                  `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/${feature?.properties.type}/multipart/${feature?.properties?.id}`,
                  formData,
                  authedRequestConfig,
                );
              } else {
                await axios.patch(
                  `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/${feature?.properties.type}/${feature?.properties?.id}`,
                  {
                    ...feature,
                    geometry: feature.properties?.geometryHasChanged ? feature.geometry : undefined,
                  },
                  authedRequestConfig,
                );
              }
            }),
          );
        }

        // Reassign to another layer
        if (reassignedFences.length > 0) {
          setMapIsLoading(true);
          await reassignedFences
            .map(r => processNextReassignedFence(r, layerId))
            .reduce((prev, cur) => prev.then(() => cur()), Promise.resolve());

          if (availableGeofences.length > 0) {
            reassignedFences.forEach(reassignedFence => {
              const foundFeature = layerData.source
                .getFeatures()
                .find((f: Feature<Geometry>) => f.get('id') === reassignedFence.id);
              if (foundFeature) {
                const newlayer = olmap
                  .getAllLayers()
                  .find(
                    l =>
                      l instanceof VectorLayer &&
                      l.getProperties().source.get('id') === reassignedFence.newLayerId,
                  );

                if (newlayer) {
                  const updatedFeature = foundFeature;
                  updatedFeature.setProperties({
                    ...updatedFeature.getProperties(),
                    layerId: newlayer.getProperties().source.get('id'),
                    layerName: newlayer.getProperties().source.get('name'),
                  });

                  const foundBuffer = layerData.source
                    .getFeatures()
                    .find((f: Feature<Geometry>) => f.get('parentId') === foundFeature.get('id'));
                  if (foundBuffer) {
                    const updatedBuffer = foundBuffer;
                    updatedBuffer.setProperties({
                      ...updatedBuffer.getProperties(),
                      layerId: newlayer.getProperties().source.get('id'),
                      layerName: newlayer.getProperties().source.get('name'),
                    });
                    newlayer.getProperties().source.addFeature(updatedBuffer as Feature<Geometry>);
                    layerData.source.removeFeature(foundBuffer as Feature<Geometry>);
                  }
                  newlayer.getProperties().source.addFeature(updatedFeature as Feature<Geometry>);
                  layerData.source.removeFeature(foundFeature as Feature<Geometry>);
                }
              }
            });

            setAvailableGeofences(
              availableGeofences.filter(f => !reassignedFences.find(r => r.id === f.id)),
            );
          }
          setReassignedFences([]);
          setMapIsLoading(false);
        }

        // Create polygons
        if (newPolygons.length > 0) {
          const createdPolygons = (
            await axios.post(
              `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/polygon/multiple`,
              { polygons: newPolygons },
              authedRequestConfig,
            )
          ).data;
          newlyCreatedPolygons.push(
            ...createdPolygons.map(
              (p: { id: string; name: string; parentId: string | undefined }) => {
                return {
                  id: p.id,
                  name: p.name,
                  parentId: p.parentId,
                };
              },
            ),
          );
        }

        // Ditto comment above re: https://geomoby.atlassian.net/browse/LTP-1559.
        // As it turns out /multiple is stil required for breach/buffer relationships.
        // Create Mulipolygons, Lines
        const newFeatures = [...newMultipolygons, ...newLines];
        if (newFeatures.length > 0) {
          const newlyCreatedFeatures = await Promise.all(
            newFeatures.map(async (feature: GeoJSONFeature) => {
              if (feature?.properties.type !== FenceEntityType.Line) {
                const formData = geofenceToMulipartFormData(
                  feature.geometry,
                  feature.properties ?? {},
                );
                return (
                  await axios.post(
                    `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/${feature?.properties.type}/multipart`,
                    formData,
                    authedRequestConfig,
                  )
                ).data;
              } else {
                return (
                  await axios.post(
                    `${triggersUrl}/${cid}/${pid}/geofences/${layerId}/${feature?.properties.type}`,
                    feature,
                    authedRequestConfig,
                  )
                ).data;
              }
            }),
          );
          newlyCreatedMultiPolygons = newlyCreatedFeatures.filter(
            f => f.points.type === GeoJsonType.Multipolygon,
          );
          newlyCreatedLines = newlyCreatedFeatures.filter(
            f => f.points.type === GeoJsonType.Linestring,
          );
        }

        await resetLayerChanges(olmap);
        setSelectedLayer({ id: layerId, name: layerData.name });
        setRefreshSearch(true);
        setSaveNotification({ id: SaveResult.SUCCESS, action: 'Save' });
      } catch (error) {
        const errorMessage = normaliseErrorMessage(error as AxiosError, EntityType.Geofence);
        await resetLayerChanges(olmap);
        setSaveNotification({
          id: SaveResult.FAIL,
          action: 'Save',
          message: errorMessage,
        });
      }

      const layer = getLayerFromMap(olmap, layerId);
      if (layer) {
        layer
          .getSource()
          ?.getFeatures()
          ?.forEach((feature: Feature<Geometry>) => {
            feature.setProperties({ updated: undefined, fresh: undefined });
            const newFence = [
              ...newlyCreatedPolygons,
              ...newlyCreatedMultiPolygons,
              ...newlyCreatedLines,
            ].find(n => n.name === feature.get('name'));

            if (newFence) {
              feature.set('id', newFence.id);
              if (newFence.parentId) {
                feature.set('parentId', newFence.parentId);
              }
            }
          });
        setLayerIds([
          { id: layerId, name: layerData?.name },
          ...layerIds.filter(lyr => lyr.id !== selectedLayer?.id),
        ]);
        setSelectedLayer({ id: layerId, name: layerData.name });
      }

      setSelectedGeofence(undefined);
      setLayersHaveChanged(false);
      deselectFences(olmap);
      reassignedFences.map(r => findFeature(olmap, r.id, r.newLayerId, r.type));
      olmap.getView().adjustZoom(-1);
      olmap.getView().adjustZoom(+1);
      return layerId;
    },
    [
      authedRequestConfig,
      availableGeofences,
      cid,
      findFeature,
      layerIds,
      pid,
      processNextReassignedFence,
      reassignedFences,
      renamingLayer,
      resetLayerChanges,
      selectedLayer,
      setSaveNotification,
      triggersUrl,
    ],
  );

  const saveLayerChanges = useCallback(
    async (olmap: OlMap): Promise<string | undefined> => {
      if (!selectedLayer) return;
      const foundLayer = getLayerFromMap(olmap, selectedLayer.id);
      if (!foundLayer) return;

      setMapIsLoading(true);
      if (selectedLayer?.id === MICROFENCE_LAYER_ID) {
        // Validate Microfences
        let featureWithDuplicateAssetId: Feature<Geometry> | undefined;
        const microfences = foundLayer.getSource().getFeatures();

        if (microfences) {
          const featureWithUndefinedAssetId = microfences.find(microfence => {
            const assetId = microfence.get('assetId');
            const type = microfence.get('type');
            if (type === MicrofenceEntity.Beacon)
              return !assetId?.uuid || assetId?.major === undefined || assetId?.minor === undefined;

            return (
              (type === MicrofenceEntity.Gateway && !assetId?.mqttgatewayId) ||
              (type === MicrofenceEntity.Device && !assetId?.deviceId)
            );
          });
          if (featureWithUndefinedAssetId) {
            setOpenDialog({
              type: DialogType.DirtySave,
              title:
                featureWithUndefinedAssetId.get('type') === MicrofenceEntity.Beacon
                  ? `Beacon IDs are required`
                  : `${
                      featureWithUndefinedAssetId.get('type') === MicrofenceEntity.Gateway
                        ? jsUcFirst(MicrofenceEntity.Gateway)
                        : jsUcFirst(MicrofenceEntity.Device)
                    } ID is required`,
            });
          }

          const featureWithUndefinedName = foundFeatureWithUndefinedName(
            microfences,
            EntityType.Microfence,
            setOpenDialog,
          );
          const featureWithDuplicateName = foundFeatureWithDuplicateName(
            microfences,
            EntityType.Microfence,
            setOpenDialog,
          );

          if (!featureWithUndefinedAssetId) {
            microfences.forEach(microfence => {
              const foundDuplicateId = getLayerFromMap(olmap, MICROFENCE_LAYER_ID)
                ?.getSource()
                ?.getFeatures()
                ?.find(f => {
                  const differentId = f.get('id') !== microfence.get('id');
                  if (!differentId) return false;

                  const duplicateBeaconIds =
                    !!f.get('assetId').uuid &&
                    f.get('assetId').uuid === microfence.get('assetId').uuid &&
                    !!f.get('assetId').major &&
                    f.get('assetId').major === microfence.get('assetId').major &&
                    !!f.get('assetId').minor &&
                    f.get('assetId').minor === microfence.get('assetId').minor;
                  const duplicateMqttGatewayId =
                    !!f.get('assetId').mqttgatewayId &&
                    f.get('assetId').mqttgatewayId === microfence.get('assetId').mqttgatewayId;
                  const duplicateDeviceId =
                    !!f.get('assetId').deviceId &&
                    f.get('assetId').deviceId === microfence.get('assetId').deviceId;

                  return duplicateBeaconIds || duplicateMqttGatewayId || duplicateDeviceId;
                });
              if (foundDuplicateId) featureWithDuplicateAssetId = microfence;
            });

            if (featureWithDuplicateAssetId !== undefined && !featureWithUndefinedName) {
              setOpenDialog({
                type: DialogType.DirtySave,
                title: featureWithDuplicateAssetId
                  ? featureWithDuplicateAssetId.get('type') === MicrofenceEntity.Beacon
                    ? 'UUID, Major and Minor combination must be unique'
                    : featureWithDuplicateAssetId.get('type') === MicrofenceEntity.Gateway
                    ? 'MQTT Gateway is already in use'
                    : 'Microfence ID must be unique'
                  : '',
              });
            }
          }

          const dirtyFence =
            featureWithUndefinedAssetId ??
            featureWithUndefinedName ??
            featureWithDuplicateName ??
            featureWithDuplicateAssetId;
          if (dirtyFence) {
            setSelectedMicrofence(dirtyFence?.getProperties() as GridRowData);
            return;
          }
          setOpenDialog(undefined);
        }
        setSelectedMicrofence(undefined);
        return await saveMicrofenceLayerChanges(olmap);
      } else {
        // Validate Geofences
        let featureWithDuplicateName;
        let featureWithEmptyGeomobyPropertyName;
        let featureWithIncompleteOverride;
        let featureWithDuplicateOverride;
        let featureWithInvalidPropertyOrOverride;
        let featureWithInvalidBreachGeometry;
        let layerIsDirty = false;

        const features = foundLayer
          .getSource()
          .getFeatures()
          .filter(feature => !feature.get('isMeasurementTool'));
        if (features) {
          const layerNotSelected = selectedGeofence?.layerId === UNKNOWN_LAYER;
          if (layerNotSelected) {
            setOpenDialog({
              type: DialogType.DirtySave,
              title: layerNotSelected
                ? 'A group has not yet been selected. Please assign one to this geofence.'
                : '',
            });
            return;
          }

          const properties = [
            ...(await API.triggers.getProjectProperties({
              clientId: cid,
              projectId: pid,
              Authorization,
            })),
            ...(await API.triggers.getGeomobyProperties({
              clientId: cid,
              projectId: pid,
              Authorization,
            })),
          ];
          const featureWithUndefinedName = foundFeatureWithUndefinedName(
            features,
            EntityType.Geofence,
            setOpenDialog,
          );
          layerIsDirty = !!featureWithUndefinedName;

          if (!layerIsDirty) {
            featureWithDuplicateName = foundFeatureWithDuplicateName(
              features,
              EntityType.Geofence,
              setOpenDialog,
            );
            layerIsDirty = !!featureWithDuplicateName;
          }
          if (!layerIsDirty) {
            featureWithEmptyGeomobyPropertyName = foundFeatureWithEmptyGeomobyPropertyValue(
              features,
              setOpenDialog,
            );
            layerIsDirty = !!featureWithEmptyGeomobyPropertyName;
          }
          if (!layerIsDirty) {
            featureWithIncompleteOverride = foundFeatureWithIncompleteOverride(
              features,
              setOpenDialog,
            );
            layerIsDirty = !!featureWithIncompleteOverride;
          }
          if (!layerIsDirty) {
            featureWithDuplicateOverride = foundFeatureWithDuplicateOverride(
              features,
              setOpenDialog,
            );
            layerIsDirty = !!featureWithDuplicateOverride;
          }

          if (!layerIsDirty) {
            featureWithInvalidPropertyOrOverride = foundFeatureWithInvalidPropertyOrOverride(
              features,
              properties,
              setOpenDialog,
            );
            layerIsDirty = !!featureWithInvalidPropertyOrOverride;
          }
          if (!layerIsDirty) {
            featureWithInvalidBreachGeometry = foundFeatureWithInvalidBreachGeometry(
              olmap,
              foundLayer,
            );
            layerIsDirty = !!featureWithInvalidBreachGeometry;
            if (featureWithInvalidBreachGeometry) {
              setOpenDialog({
                type: DialogType.DirtySave,
                title: `The breach zone "${featureWithInvalidBreachGeometry.get(
                  'name',
                )}" is not completely contained within its buffer zone. \nPlease amend the geometry.`,
              });
              setEditMode(EntityType.Geofence);
            }
          }
          if (layerIsDirty) {
            const dirtyFence =
              featureWithUndefinedName ??
              featureWithDuplicateName ??
              featureWithEmptyGeomobyPropertyName ??
              featureWithIncompleteOverride ??
              featureWithDuplicateOverride ??
              featureWithInvalidPropertyOrOverride ??
              featureWithInvalidBreachGeometry;
            setSelectedGeofence(dirtyFence?.getProperties() as GridRowData);
            return;
          }
          setOpenDialog(undefined);
        }
        return await saveGeofenceLayerChanges(olmap);
      }
    },
    [
      Authorization,
      cid,
      pid,
      saveGeofenceLayerChanges,
      saveMicrofenceLayerChanges,
      selectedGeofence?.layerId,
      selectedLayer,
    ],
  );

  const stopEditingFeature = useCallback((olmap: OlMap) => {
    if (!editRef.current) {
      console.warn('UI should not be able to unset editing while not in editing mode');
      return;
    }
    if (!olmap) {
      console.warn('UI should not be able to unset editing while no map exists');
      return;
    }

    const collections = new Collection(
      olmap
        .getInteractions()
        .getArray()
        .filter(
          (i: Interaction) =>
            !(i instanceof Modify || i instanceof Draw) ||
            i instanceof Snap ||
            i instanceof Translate,
        ),
    );
    olmap.getInteractions().clear();
    collections.forEach(i => olmap.addInteraction(i));
    editRef.current = undefined;
    setEditMode(undefined);
    setEditType(undefined);
    setDrawType(undefined);
    setLayerIds(prev => prev.filter(lyr => lyr.id !== UNKNOWN_LAYER));
    document.removeEventListener('keyup', removeLastDrawnSegment);
  }, []);

  const createNewLayer = useCallback(
    (olmap: OlMap, id: string, name: string) => {
      if (!olmap) {
        console.warn('UI is unable to create a new layer when no map exists.');
        return;
      }

      const newSource = new VectorSource();
      const newLayer = new VectorLayer({
        source: newSource,
        className: GEOFENCE_LAYER,
        style: updateGeofencesStyle(styleCache, selectedGeofence?.id),
        properties: {
          name,
          id,
          geomobyProperties: {},
        },
      });
      setLayerIds([{ id, name }, ...layerIds]);
      olmap.addLayer(newLayer);
      setSelectedLayer({ id, name });
      layerChanged(olmap);
    },
    [layerChanged, layerIds, selectedGeofence?.id],
  );

  const deleteLayer = useCallback(
    async (olmap: OlMap): Promise<void> => {
      if (!selectedLayer) {
        console.warn('UI should not trigger delete when no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to delete a layer while no map exists');
        return;
      }

      const foundLayer = getLayerFromMap(olmap, undefined, selectedLayer.name);
      if (foundLayer) {
        olmap.removeLayer(foundLayer);
      }
      const freshLayer = olmap
        .getAllLayers()
        .find(
          l =>
            l instanceof VectorLayer &&
            (l.getSource()?.get('id') === FRESH_LAYER || l.get('id') === FRESH_LAYER),
        );
      if (freshLayer) {
        olmap.removeLayer(freshLayer);
      }

      setLayerIds(layerIds.filter(lyr => lyr.id !== selectedLayer.id));
      try {
        await axios.delete(
          `${triggersUrl}/${cid}/${pid}/geofences/${selectedLayer.id}`,
          authedRequestConfig,
        );
        setSaveNotification({ id: SaveResult.SUCCESS, action: 'Delete' });
      } catch (error) {
        const errorMessage = normaliseErrorMessage(error as AxiosError, EntityType.Layer);
        setSaveNotification({
          id: SaveResult.FAIL,
          action: 'Delete',
          message: errorMessage,
        });
      }
      deletedFenceIdsRef.current = [];
      setSelectedLayer(undefined);
      setSelectedGeofence(undefined);
      setSelectedMicrofence(undefined);
      setEditMode(undefined);
      deselectFences(olmap);
      setDrawType(undefined);
      layerIds.forEach(layer => changeVisibility(olmap, layer.id, layer.id !== selectedLayer.id));
    },
    [
      authedRequestConfig,
      changeVisibility,
      cid,
      layerIds,
      pid,
      selectedLayer,
      setSaveNotification,
      triggersUrl,
    ],
  );

  const deleteFeature = useCallback(
    async (olmap: OlMap, fence: GridRowData, type: FenceEntityType | undefined): Promise<void> => {
      const fenceId = fence.id ?? fence.get('id');
      const layerId = fence.layerId ?? fence.get('layerId');
      const layer = getLayerFromMap(olmap, layerId);
      if (!layer) {
        console.warn('UI should not be able to delete a feature nonexistent layer.');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to delete a feature while no map exists');
        return;
      }

      const microfences = getLayerFromMap(olmap, MICROFENCE_LAYER_ID)?.getSource()?.getFeatures();
      if (!microfences) return;

      const feature = await findFeature(olmap, fenceId, layerId, type);
      if (!feature) {
        console.warn(`Failed to find fence to perform deletion [${fenceId}]`);
        return;
      }

      const bufferFeature =
        feature.get('zone') === FenceZone.breach
          ? layer
              .getSource()
              .getFeatures()
              .find(f => f.get('parentId') === feature.get('id'))
          : undefined;

      layer.getSource().removeFeature(feature);
      setSelectedGeofence(undefined);
      setSelectedMicrofence(undefined);

      if (layerId !== MICROFENCE_LAYER_ID && availableGeofences.length > 0) {
        setAvailableGeofences(availableGeofences.filter(f => f.id !== fenceId));
      } else if (
        selectedLayer?.id === MICROFENCE_LAYER_ID &&
        (availableMicrofences.length > 0 || microfences.length)
      ) {
        setAvailableMicrofences(availableMicrofences.filter(f => f.id !== fenceId));
      }

      if (type && !deletedFenceIdsRef.current.find(d => d.id === fenceId)) {
        deletedFenceIdsRef.current = [...deletedFenceIdsRef.current, { id: fenceId, type }];
      }
      if (bufferFeature) deleteFeature(olmap, bufferFeature, FenceEntityType.Polygon);
    },
    [availableGeofences, availableMicrofences, findFeature, selectedLayer?.id],
  );

  const setFreshFenceName = useCallback(
    async (layerId: string, type: EntityType, freshNames: string[]) => {
      // This is a little bit ugly given we still don't have paginated microfences.
      // Also, if there more than 500 geofences that satisfy these constraints, perhaps we want to think about increasing the perPage limit and adding a 'start with' condition.
      const nameType = jsUcFirst(type);
      const unspecifiedLayerId = layerId === ALL_LAYERS || layerId === UNKNOWN_LAYER || !layerId;
      let names: string[] = [];
      if (type === EntityType.Microfence) {
        names = Array.from(
          new Set([
            ...freshNames,
            ...((
              await API.triggers.paginateMicrofences({
                projectId: pid,
                clientId: cid,
                Authorization,
                query: {
                  id: unspecifiedLayerId ? undefined : layerId,
                  name: jsUcFirst(EntityType.Microfence),
                  page: 1,
                  perPage: 500,
                },
              })
            )?.results?.map(microfence => microfence.name) ?? []),
          ]),
        );
      } else {
        names = [
          ...freshNames,
          ...((
            await API.triggers.paginateGeofences({
              projectId: pid,
              clientId: cid,
              Authorization,
              filter: {
                layerId: unspecifiedLayerId ? undefined : layerId,
                searchName: jsUcFirst(EntityType.Geofence),
              },
              pageNumber: 1,
              perPage: 500,
            })
          ).geofences?.map(geofence => geofence.name) ?? []),
        ];
      }

      return `${nameType}${Number(
        Math.max(
          ...names
            .filter(name => name?.startsWith(name))
            .map(n => Number(n.replace(nameType, '')))
            .filter(n => !isNaN(n)),
          0,
        ) + 1,
      )}`;
    },
    [Authorization, cid, pid],
  );

  const editFeature = useCallback(
    (olmap: OlMap) => {
      if (editRef.current) {
        console.warn('UI should not be able to set editing while already editing');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to edit a feature while no map exists');
        return;
      }

      let foundLayer: VectorLayer<VectorSource<Geometry>> | undefined;
      if (!selectedLayer || selectedLayer?.id === ALL_LAYERS) {
        createNewLayer(olmap, UNKNOWN_LAYER, UNKNOWN_LAYER);
        foundLayer = getLayerFromMap(olmap, undefined, UNKNOWN_LAYER);
      } else if (selectedLayer) {
        foundLayer = getLayerFromMap(olmap, selectedLayer.id);
      }
      if (!foundLayer) {
        console.warn('UI should not be able to set editing while no layer selected');
        return;
      }

      setClearFilter(true);
      if (
        (selectedGeofence &&
          geometryTypeOfEntity(selectedGeofence) !== FenceEntityType.Multipolygon) ||
        selectedMicrofence
      ) {
        setSelectedGeofence(undefined);
        setSelectedMicrofence(undefined);
        setAvailableGeofences([]);
        setAvailableMicrofences([]);
        deselectFences(olmap);
      }
      unsetMapClickSelection(olmap);

      foundLayer.getSource().on('addfeature', async e => {
        if (!foundLayer?.getSource()) {
          console.warn('UI should not be able to set editing while no layer selected');
          return;
        }
        if (!e.feature) {
          console.warn('UI should not be able add an undefined feature');
          return;
        }
        if (e.feature.get('id') || e.feature.get('isMeasurementTool')) return;

        const layerId =
          (e.target as VectorSource<Geometry>)?.get('id') ??
          (!selectedLayer?.id || selectedLayer.id === ALL_LAYERS
            ? UNKNOWN_LAYER
            : selectedLayer.id);

        if (
          selectedLayer?.id !== MICROFENCE_LAYER_ID &&
          !olmap
            .getAllLayers()
            .find(
              lyr =>
                lyr instanceof VectorLayer &&
                (lyr.get('id') === UNKNOWN_LAYER || lyr.getSource()?.get('id') === UNKNOWN_LAYER),
            )
        ) {
          const foundLayer = getLayerFromMap(olmap, layerId);
          if (foundLayer && (layerId !== selectedLayer?.id || (layerId && !selectedLayer))) {
            setSelectedLayer({
              id: layerId,
              name: foundLayer.get('name') ?? foundLayer.getSource()?.get('name'),
            });
          }
        }

        const currentSelectedMultipolygon = foundLayer
          .getSource()
          .getFeatures()
          .find(f => !!f.get('selected'));
        if (
          e.feature?.getGeometry().getType().toLowerCase() === FenceEntityType.Multipolygon &&
          currentSelectedMultipolygon &&
          geometryTypeOfEntity(currentSelectedMultipolygon.getProperties()) ===
            FenceEntityType.Multipolygon
        ) {
          let coordinates: number[][][][] | undefined = (
            currentSelectedMultipolygon.getGeometry() as MultiPolygon
          )?.getCoordinates();
          if (
            coordinates.find(
              c =>
                JSON.stringify(c) === JSON.stringify(e.feature?.getGeometry().getCoordinates()[0]),
            )
          )
            return;

          const newFeature = e.feature?.getGeometry();
          if (!newFeature || !currentSelectedMultipolygon.getGeometry()) return;
          const currentFeature = currentSelectedMultipolygon.getGeometry() as MultiPolygon;
          const newCoords = (newFeature as MultiPolygon).getCoordinates()[0];
          const pointsIntersect = newCoords.every((coord: number[][]) =>
            coord.map(c => currentFeature.intersectsCoordinate(c)).find(c => !!c),
          );
          if (pointsIntersect && editTypeRef.current === EditType.Cut) {
            coordinates = mergeMultiPolygons(
              currentSelectedMultipolygon as Feature<MultiPolygon>,
              e.feature,
            );
          } else {
            coordinates.push(e.feature.getGeometry().getCoordinates()[0]);
          }

          if (coordinates) {
            const validatedGeometry = await API.triggers.makeMultipolygonValid({
              coordinates: transformMultipolygon(coordinates, 'EPSG:3857'),
              layerId,
              projectId: pid,
              clientId: cid,
              Authorization: `Bearer ${accessJwtToken}`,
            });
            currentSelectedMultipolygon.setGeometry(
              new MultiPolygon(transformMultipolygon(validatedGeometry.coordinates, 'EPSG:4326')),
            );
          }

          currentSelectedMultipolygon?.set(GEOMETRY_HAS_CHANGED, true);
          if (currentSelectedMultipolygon.get('points')) {
            currentSelectedMultipolygon.get('points')?.coordinates.push([
              e.feature
                .getGeometry()
                .getCoordinates()[0][0]
                .map((c: [number, number]) => transform(c, 'EPSG:3857', 'EPSG:4326')),
            ]);
            if (!currentSelectedMultipolygon.get('id')?.includes(FRESH)) {
              currentSelectedMultipolygon.set(UPDATED, true);
            }
          }
          setSelectedGeofence(currentSelectedMultipolygon.getProperties());

          const featureToRemove = foundLayer
            .getSource()
            .getFeatures()
            .filter(f => !f.get('isMeasurementTool'))
            .find((feature: Feature<Geometry>) => feature.get('id') === e.feature?.get('id'));
          if (featureToRemove) {
            foundLayer.getSource().removeFeature(featureToRemove);
          }
          return;
        }
        currentSelectedMultipolygon?.set('selected', false);

        if (layerId !== MICROFENCE_LAYER_ID) {
          e.feature.setProperties({
            ...e.feature.getProperties(),
            id: `${FRESH}-${new Date().getTime()}`,
            fresh: true,
            selected: true,
            layerId,
            layerName: foundLayer.getSource()?.get('name') ?? foundLayer.get('name'),
          });
          setSelectedGeofence(e.feature.getProperties() as GridRowData);
          freshFencesRef.current = [...freshFencesRef.current, e.feature.getProperties()];
          setFreshFences(freshFencesRef.current);
        }
      });
      const draw = new Draw({
        type: GeometryType.POLYGON,
        source: foundLayer.getSource(),
        stopClick: true,
      });

      draw.on('drawend', () => {
        layerChanged(olmap);
      });
      const modify = new Modify({
        source: foundLayer.getSource(),
      });

      const translate = new Translate({
        condition: event => {
          return primaryAction(event) && platformModifierKeyOnly(event);
        },
        layers: olmap.getAllLayers(),
      });

      const select = new Select({
        condition: click,
        layers: olmap.getAllLayers(),
      });

      if (foundLayer.getSource()) {
        modify.on('modifyend', (e: ModifyEvent) =>
          featureModified(olmap, e, foundLayer?.getSource() as VectorSource<Geometry>),
        );
      }

      const es = {
        draw: draw,
        modify: modify,
        modifyScaleRot: modify,
        snap: new Snap({
          source: foundLayer.getSource(),
        }),
        translate: translate,
        select: select,
      };

      editRef.current = es;
      addDrawInteraction(olmap, editRef.current);
      document.addEventListener('keyup', removeLastDrawnSegment);
    },
    [
      accessJwtToken,
      cid,
      createNewLayer,
      featureModified,
      layerChanged,
      pid,
      selectedGeofence,
      selectedLayer,
      selectedMicrofence,
    ],
  );

  const setMapClickDeselection = useCallback(
    (olmap: OlMap) => {
      olmap.on('click', e => {
        const fences: GeoJSONFeature[] =
          olmap.getFeaturesAtPixel(e.pixel).filter(f => !f.get(IS_INVISIBLE)) ?? [];

        if (editTypeRef.current !== EditType.Delete) {
          setEditMode(undefined);
          deselectFences(olmap);
          if (editRef.current) return;
          if (freshFencesRef.current.find(f => f.layerId === UNKNOWN_LAYER)) {
            setOpenGenericDialog(true);
            return;
          }
          if (fences.length === 0) {
            setSelectedGeofence(undefined);
            setSelectedMicrofence(undefined);
            setDeselectFence(true);
            setRefreshSearch(true);
            if (!layersHaveChangedRef.current) {
              setSelectedLayer(undefined);
              resetLayerChanges(olmap);
              return;
            }
          }
        }

        const selectableFences = fences.filter(f => !!f.get('layerId'));
        const selectedFence =
          getSmallestAreaFeature(selectableFences) ?? getSmallestAreaFeature(fences);

        if (!selectedFence) return;

        const layerId = selectedFence.get('layerId');
        if (!layerId) return;

        if (layerId === MICROFENCE_LAYER_ID) {
          selectedFence.set('selected', true);
          setSelectedLayer({ name: MICROFENCE_LAYER_LABEL, id: MICROFENCE_LAYER_ID });
          setSelectedMicrofence(selectedFence?.getProperties());
          setSearchType({ id: SearchTypeIDs.Microfences, value: SearchTypeValue.Microfences });
        } else {
          if (showGhostGeofences) return;
          setShowGhostGeofences(false);
          const foundLayer = getLayerFromMap(olmap, layerId);
          if (!foundLayer) return;

          // Avoid being able to delete parts of other Multipolygons while one is already selected.
          const featureAlreadySelected = foundLayer
            .getSource()
            .getFeatures()
            .find(f => f.get('selected'));
          if (editTypeRef.current === EditType.Delete && !!featureAlreadySelected) {
            if (featureAlreadySelected?.get('id') !== selectedFence.get('id')) return;
          }

          selectedFence.set('selected', true);
          setSelectedLayer(
            foundLayer ? { id: layerId, name: foundLayer.getSource().get('name') } : undefined,
          );
          setSelectedGeofence(selectedFence?.getProperties() as GridRowData);
          setSearchType({ id: SearchTypeIDs.Geofences, value: SearchTypeValue.Geofences });
          if (geometryTypeOfEntity(selectedFence.getProperties()) === FenceEntityType.Polygon) {
            const geometry = selectedFence?.getGeometry();
            if (geometry && selectedLayer) {
              setHasFences(
                polygonsContainedWithinPolygon(geometry, foundLayer?.getSource()).length === 1,
              );
            }
          }
        }

        setSelectedFromMap(true);
        layerIds.forEach(layer =>
          changeVisibility(olmap, layer.id, layer.id === layerId || showGhostGeofences),
        );

        // Delete individual parts of Multipolygons
        if (
          (e.originalEvent.ctrlKey || e.originalEvent.metaKey) &&
          selectedFence &&
          editTypeRef.current === EditType.Delete
        ) {
          const geometry = selectedFence.getGeometry();
          const foundLayer = getLayerFromMap(olmap, layerId);
          if (!foundLayer) return;
          if (geometry instanceof MultiPolygon) {
            const clickedCoord = e.coordinate;
            const newGeometry = removePolygonFromMultiPolygon(geometry, clickedCoord);
            if (newGeometry) {
              selectedFence.setGeometry(newGeometry);
            }
            setLayersHaveChanged(true);
            selectedFence.set(UPDATED, true);
            selectedFence.set(GEOMETRY_HAS_CHANGED, true);
            setDrawType(DrawType.Multipolygon);
          }
        }
      });
    },
    [changeVisibility, layerIds, resetLayerChanges, selectedLayer, showGhostGeofences],
  );

  const createScalableRotatableLayer = (
    olmap: OlMap,
  ): VectorLayer<VectorSource<Geometry>> | undefined => {
    if (!selectedLayer) return;
    const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
    return new VectorLayer({
      className: SCALABLE_ROTATABLE_LAYER,
      source: foundLayer?.getSource() ?? new VectorSource(),
      style: feature => {
        const type = geometryTypeOfEntity(feature.getProperties());
        const zoneStyle = getGeofenceStyleFromZone({
          zone: feature.get('zone'),
          type,
          layerName: feature.get('layerName'),
        });

        const styles = [
          new Style({
            geometry: feature => {
              const modifyGeometry = feature.get('modifyGeometry');
              return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
            },
            stroke: zoneStyle.getStroke(),
            zIndex: zoneStyle.getZIndex(),
          }),
        ];
        const modifyGeometry = feature.get('modifyGeometry');
        const geometry = modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
        if (type === FenceEntityType.Line) {
          return [...styles, ...tripwiresStyle(geometry.flatCoordinates)];
        }
        return styles;
      },
    });
  };

  const updateMeasureTooltip = (olmap: OlMap) => {
    if (measureTooltipElementRef.current) {
      measureTooltipElementRef.current.parentNode?.removeChild(measureTooltipElementRef.current);
    }
    measureTooltipElementRef.current = document.createElement('div');
    measureTooltipRef.current = new Overlay({
      element: measureTooltipElementRef.current,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false,
    });
    olmap.addOverlay(measureTooltipRef.current);
  };

  const addShapeChangeModifyInteration = (
    olmap: OlMap,
    source: VectorSource<Geometry>,
    type: GeofenceEntityType,
  ) => {
    if (!editRef.current) {
      console.warn('UI should not be able to modify a feature while not in editing mode');
      return;
    }
    if (!olmap) {
      console.warn('UI should not be able to modify a feature while no map exists');
      return;
    }
    if (type !== GeofenceEntityType.Multipolygon) {
      clearAllInteractions(olmap);
    }

    const features: Collection<Feature<Geometry>> = new Collection();
    source.forEachFeature(feature => {
      if (
        geometryTypeOfEntity(feature.getProperties() as GridRowData) ===
        (type as unknown as FenceEntityType)
      ) {
        features.push(feature);
      }
    });
    editRef.current.modify = new Modify({
      source: source,
      insertVertexCondition: type === GeofenceEntityType.Line ? never : always,
      features,
    });
    if (source.get('name') === TRACKING_BOUNDS && type === GeofenceEntityType.Polygon) {
      editRef.current.modify = interactionStyle(
        Interactions.Modify,
        source,
        GeometryType.POLYGON,
        WHITE,
      ) as Modify;
    } else if (type === GeofenceEntityType.Multipolygon) {
      editRef.current.modify = interactionStyle(
        Interactions.Modify,
        source,
        GeometryType.MULTI_POLYGON,
        CLEARED_ZONE,
      ) as Modify;
    }

    editRef.current.modify.on('modifyend', (e: ModifyEvent) => {
      featureModified(olmap, e, source);
      e.features.forEach(f => {
        const geometry = f.getGeometry();
        if (!(geometry instanceof Geometry)) return;
        if (type === GeofenceEntityType.Polygon) {
          const containsFeatures = polygonsContainedWithinPolygon(geometry, source);
          setHasFences(containsFeatures.length === 1);
          if (
            featureHasInvalidBreachBufferGeometry(
              olmap,
              source,
              f as unknown as Feature<Geometry>,
              containsFeatures,
            )
          ) {
            setOpenDialog({
              type: DialogType.BreachZoneNotAllowed,
              title: 'Breach Zone must be within BufferZone',
            });
          }
        }
        const feature = f as Feature<Geometry>;
        feature.set(GEOMETRY_HAS_CHANGED, true);
        setSelectedGeofence({
          ...(feature.getProperties() as GridRowData),
          selected: true,
        });
      });
      if (type !== GeofenceEntityType.Line) return;
    });
    editRef.current.modify.on('modifystart', (e: ModifyEvent) => {
      const feature = e.features.item(0) as Feature<Geometry>;
      feature.set('selected', true);
      feature.set(GEOMETRY_HAS_CHANGED, true);
      setSelectedGeofence({
        ...feature.getProperties(),
      } as GridRowData);
    });
    addModifyInteraction(olmap, editRef.current);
  };

  const addScaleRotModifyInteration = (
    olmap: OlMap,
    source: VectorSource<Geometry>,
    type: GeofenceEntityType,
  ) => {
    clearAllInteractions(olmap);
    if (!editRef.current) {
      console.warn('UI should not be able to modify a feature while not in editing mode');
      return;
    }
    if (!olmap) {
      console.warn('UI should not be able to modify a feature while no map exists');
      return;
    }

    editRef.current.modifyScaleRot = new Modify({
      source: source,
      condition: event => {
        return primaryAction(event) && !platformModifierKeyOnly(event);
      },
      deleteCondition: never,
      insertVertexCondition: type === GeofenceEntityType.Line ? never : always,
      style: defaultScaleRotFenceStyle(),
    });

    const scalableRotatableLayer = createScalableRotatableLayer(olmap);
    if (!scalableRotatableLayer) return;
    editRef.current.translate = new Translate({
      condition: event => {
        return primaryAction(event) && platformModifierKeyOnly(event);
      },
      layers: [scalableRotatableLayer],
    });
    olmap.addLayer(scalableRotatableLayer);

    editRef.current.modifyScaleRot.on('modifystart', (e: ModifyEvent) => {
      olmap.getAllLayers().forEach(layer => {
        if (!(layer instanceof VectorLayer) || layer.getClassName() === SCALABLE_ROTATABLE_LAYER)
          return;
        layer.setVisible(false);
      });

      e.features.forEach(feature => {
        const geom = feature.getGeometry();
        if (!(geom instanceof Geometry)) return;
        (feature as Feature<Geometry>).set('modifyGeometry', { geometry: geom.clone() }, true);
        setSelectedGeofence({
          ...((feature as Feature<Geometry>).getProperties() as GridRowData),
          selected: true,
        });
      });
      if (type !== GeofenceEntityType.Line) return;
    });

    editRef.current.modifyScaleRot.on('modifyend', (e: ModifyEvent) => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) return;
      changeVisibility(olmap, selectedLayer.id, true);
      e.features.forEach(feature => {
        const geom = feature.getGeometry();
        if (!(geom instanceof Geometry)) return;
        const modifyGeometry = feature.get('modifyGeometry');
        if (modifyGeometry) {
          (feature as Feature<Geometry>).setGeometry(modifyGeometry.geometry);
          (feature as Feature<Geometry>).unset('modifyGeometry', true);
          if (type === GeofenceEntityType.Polygon) {
            const containsFeatures = polygonsContainedWithinPolygon(
              modifyGeometry.geometry,
              foundLayer.getSource(),
            );
            setHasFences(containsFeatures.length === 1);
            if (
              featureHasInvalidBreachBufferGeometry(
                olmap,
                source,
                feature as Feature<Geometry>,
                containsFeatures,
              )
            ) {
              setOpenDialog({
                type: DialogType.BreachZoneNotAllowed,
                title: 'Breach Zone must be within BufferZone',
              });
            }
          }
          setSelectedGeofence({
            ...((feature as Feature<Geometry>).getProperties() as GridRowData),
            selected: true,
          });
        }
      });

      featureModified(olmap, e, source);
      if (type !== GeofenceEntityType.Line) return;
    });

    olmap.addInteraction(editRef.current.modifyScaleRot);
    olmap.addInteraction(editRef.current.translate);
  };

  const addShapeMoveModifyInteration = (
    olmap: OlMap,
    source: VectorSource<Geometry>,
    type: GeofenceEntityType,
  ) => {
    if (!editRef.current) {
      console.warn('UI should not be able to modify a feature while not in editing mode');
      return;
    }
    if (!olmap) {
      console.warn('UI should not be able to modify a feature while no map exists');
      return;
    }
    if (type !== GeofenceEntityType.Multipolygon) {
      clearAllInteractions(olmap);
    }

    const features: Collection<Feature<Geometry>> = new Collection();
    const addAllFeatureFromLayer = (features: Collection<Feature<Geometry>>) => {
      source.forEachFeature(feature => {
        if (
          geometryTypeOfEntity(feature.getProperties() as GridRowData) ===
          (type as unknown as FenceEntityType)
        ) {
          features.push(feature);
        }
      });
    };
    addAllFeatureFromLayer(features);
    editRef.current.translate = new Translate({
      features,
    });
    editRef.current.translate.on('translatestart', e => {
      const el = olmap.getTargetElement();
      if (el) el.style.cursor = 'grabbing';

      const startCoordinates = e.coordinate;
      let feature = e.features.item(0) as Feature<Geometry>;
      const resolution = olmap.getView().getResolution() ?? 1;
      const pixelTolerance = 10;
      const toleranceMapUnits = resolution * pixelTolerance;

      e.features.forEach(f => {
        const geom = f.getGeometry();
        let isDragging: boolean;
        if (geom instanceof LineString) {
          const closest = geom.getClosestPoint(startCoordinates);
          const dist = Math.hypot(
            startCoordinates[0] - closest[0],
            startCoordinates[1] - closest[1],
          );
          isDragging = dist <= toleranceMapUnits;
        } else {
          isDragging = featureContainsPoint(geom, startCoordinates);
        }
        if (isDragging) {
          feature = f;
        }
      });
      if (!feature) return;

      features.clear();
      features.push(feature);

      feature.set('selected', true);
      feature.set(GEOMETRY_HAS_CHANGED, true);

      setSelectedGeofence({
        ...feature.getProperties(),
      } as GridRowData);
    });
    editRef.current.translate.on('translateend', e => {
      olmap.getTargetElement().style.cursor = '';
      featureModified(olmap, e, source);
      e.features.forEach(f => {
        const geometry = f.getGeometry();
        if (!(geometry instanceof Geometry)) return;
        if (type === GeofenceEntityType.Polygon) {
          const containsFeatures = polygonsContainedWithinPolygon(geometry, source);
          setHasFences(containsFeatures.length === 1);
          if (featureHasInvalidBreachBufferGeometry(olmap, source, f, containsFeatures)) {
            setOpenDialog({
              type: DialogType.BreachZoneNotAllowed,
              title: 'Breach Zone must be within BufferZone',
            });
          }
        }
        const feature = f as Feature<Geometry>;
        feature.set(GEOMETRY_HAS_CHANGED, true);
        setSelectedGeofence({
          ...(feature.getProperties() as GridRowData),
          selected: true,
        });
      });
      features.clear();
      addAllFeatureFromLayer(features);
    });
    olmap.addInteraction(editRef.current.translate);
  };

  const removeLastDrawnSegment = (e: KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Escape') {
      editRef.current?.draw.removeLastPoint();
    }
  };

  const drawEnd = (
    olmap: OlMap,
    geometry: Geometry,
    layerSource: VectorSource<Geometry>,
    type: GeofenceEntityType,
  ) => {
    layerChanged(olmap);
    if (type === GeofenceEntityType.Polygon) {
      setHasFences(polygonsContainedWithinPolygon(geometry, layerSource).length === 1);
    }
    if (!editRef.current) return;
    // Slight hack to reset the editType. The default editType will not be active otherwise.
    setEditType(editType);
    // Needs a short interval for the new polygon to be registered as part of the layer.
    setTimeout(() => {
      addShapeChangeModifyInteration(olmap, layerSource, type);
      addDrawInteraction(olmap, editRef.current);
      if (editType !== EditType.Cut) {
        addModifyInteraction(olmap, editRef.current);
      }
    });
  };

  const microfenceDrawEnd = async (olmap: OlMap, microfence: Feature<Geometry>) => {
    deselectFences(olmap);
    layerChanged(olmap);
    microfence.set('id', `${FRESH}-${new Date().getTime()}`);
    microfence.set('layerId', MICROFENCE_LAYER_ID);
    microfence.set('layerName', MICROFENCE_LAYER_LABEL);
    microfence.set('fresh', true);
    microfence.set('selected', true);
    microfence.set('boundaryRssi', BOUNDARY_RSSI_DEFAULT);
    microfence.set('timeoutSeconds', TIMEOUT_SECONDS_DEFAULT);
    microfence.set('point', {
      type: 'Point',
      coordinates: transform(
        (microfence.getGeometry() as Point)?.getCoordinates(),
        'EPSG:3857',
        'EPSG:4326',
      ),
    });
    microfence.set(
      'name',
      await setFreshFenceName(
        MICROFENCE_LAYER_ID,
        EntityType.Microfence,
        freshFencesRef.current.map(f => f.name),
      ),
    );
    freshFencesRef.current = [...freshFencesRef.current, microfence.getProperties()];
    setFreshFences(freshFencesRef.current);
    setSelectedMicrofence(microfence.getProperties());
  };

  const updateMeasurementType = useCallback(
    (olmap: OlMap, type: MeasurementType, fenceType: 'Polygon' | 'LineString') => {
      if (!olmap || !editRef.current) return;
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      clearAllInteractions(olmap);
      if (!selectedLayer || !foundLayer) return;

      editRef.current.draw = new Draw({
        source: foundLayer.getSource(),
        type: fenceType,
        style: measurementToolPonter,
      });

      addDrawInteraction(olmap, editRef.current);
      editRef.current.draw.on('drawstart', e => {
        updateMeasureTooltip(olmap);
        let tooltipCoord = e.feature?.getGeometry().getCoordinates();
        e.feature.getGeometry().on('change', (evt: Event) => {
          const geom = evt.target;
          if (geom instanceof Polygon) {
            tooltipCoord = geom.getInteriorPoint().getCoordinates();
            const area = calculateArea(geom, type);
            if (measureTooltipElementRef.current && measureTooltipRef.current && area) {
              measureTooltipElementRef.current.innerHTML = area;
              measureTooltipElementRef.current.style.background = `rgb(${[0, 0, 0, 0.25]})`;
              measureTooltipElementRef.current.style.borderRadius = '5px';
              measureTooltipRef.current.setPosition(tooltipCoord);
            }
          } else if (geom instanceof LineString) {
            tooltipCoord = geom.getLastCoordinate();
            const distance = calculateMeasurementDistance(geom, type);
            if (measureTooltipElementRef.current && measureTooltipRef.current && distance) {
              measureTooltipElementRef.current.innerHTML = distance;
              measureTooltipElementRef.current.style.background = `rgb(${[0, 0, 0, 0.25]})`;
              measureTooltipElementRef.current.style.borderRadius = '5px';
              measureTooltipRef.current.setPosition(tooltipCoord);
            }
          }
        });
      });

      editRef.current.draw.on('drawend', e => {
        e.feature.set('isMeasurementTool', true);
        e.feature.setStyle(measurementToolOff);
        const foundFence = foundLayer
          .getSource()
          .getFeatures()
          .find(f => f.get('id') === e.feature.get('id'));
        if (foundFence) {
          foundLayer.getSource().removeFeature(foundFence);
        }
        updateMeasureTooltip(olmap);
      });
    },
    [selectedLayer],
  );

  const changeDrawShape = async (olmap: OlMap) => {
    if (!olmap || !editRef.current || !drawType) return;
    const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
    clearAllInteractions(olmap);
    if (!selectedLayer || !foundLayer) return;

    if (editType) {
      switch (editType) {
        case EditType.ShapeChange:
          addShapeChangeModifyInteration(
            olmap,
            foundLayer.getSource(),
            drawType === DrawType.Circle || drawType === DrawType.Polygon
              ? GeofenceEntityType.Polygon
              : drawType === DrawType.Multipolygon
              ? GeofenceEntityType.Multipolygon
              : GeofenceEntityType.Line,
          );
          break;
        case EditType.ScaleRot:
          addScaleRotModifyInteration(
            olmap,
            foundLayer.getSource(),
            drawType === DrawType.Circle || drawType === DrawType.Polygon
              ? GeofenceEntityType.Polygon
              : drawType === DrawType.Multipolygon
              ? GeofenceEntityType.Multipolygon
              : GeofenceEntityType.Line,
          );
          break;
        case EditType.Move:
          addShapeMoveModifyInteration(
            olmap,
            foundLayer.getSource(),
            drawType === DrawType.Circle || drawType === DrawType.Polygon
              ? GeofenceEntityType.Polygon
              : drawType === DrawType.Multipolygon
              ? GeofenceEntityType.Multipolygon
              : GeofenceEntityType.Line,
          );
          break;
      }
    }

    let name = '';
    switch (drawType) {
      case DrawType.Circle:
        if (foundLayer.getSource().get('name') === TRACKING_BOUNDS) {
          editRef.current.draw = interactionStyle(
            Interactions.Draw,
            foundLayer.getSource(),
            GeometryType.CIRCLE,
            WHITE,
          ) as Draw;
          editRef.current.modify = interactionStyle(
            Interactions.Modify,
            foundLayer.getSource(),
            GeometryType.POLYGON,
            WHITE,
          ) as Modify;
        } else {
          editRef.current.draw = new Draw({
            source: foundLayer.getSource(),
            type: GeometryType.CIRCLE,
            stopClick: true,
          });
        }
        addDrawInteraction(olmap, editRef.current);

        editRef.current.draw.on('drawstart', async () => {
          setSelectedGeofence(undefined);
          name = await setFreshFenceName(
            selectedLayer?.id,
            EntityType.Geofence,
            freshFencesRef.current.map(f => f.name),
          );
        });
        editRef.current.draw.on('drawend', async e => {
          if (foundLayer.getSource().get('name') === TRACKING_BOUNDS) e.feature.setStyle(undefined);
          drawEnd(
            olmap,
            e.feature.getGeometry(),
            foundLayer.getSource(),
            GeofenceEntityType.Polygon,
          );
          e.feature.set('name', name);
          e.feature.set('type', FenceEntityType.Polygon);
          e.feature.set('zone', FenceZone.none);
          e.feature.set('geomobyProperties', {});
          e.feature.set('geomobyOverrides', []);
          e.feature.setGeometry(fromCircle(e.feature.getGeometry() as Circle));
        });
        break;
      case DrawType.Polygon:
        if (foundLayer.getSource().get('name') === TRACKING_BOUNDS) {
          editRef.current.draw = interactionStyle(
            Interactions.Draw,
            foundLayer.getSource(),
            GeometryType.POLYGON,
            WHITE,
          ) as Draw;
          editRef.current.modify = interactionStyle(
            Interactions.Modify,
            foundLayer.getSource(),
            GeometryType.POLYGON,
            WHITE,
          ) as Modify;
          olmap.addInteraction(editRef.current.draw);
          editRef.current.draw.on('drawstart', e => {
            e.feature.setStyle(trackingFenceDrawStartStyle);
          });
        } else {
          editRef.current.draw = new Draw({
            source: foundLayer.getSource(),
            type: GeometryType.POLYGON,
            stopClick: true,
          });
          olmap.addInteraction(editRef.current.draw);
        }
        addDrawInteraction(olmap, editRef.current);
        foundLayer.setStyle(updateGeofencesStyle(new Map()));

        editRef.current.draw.on('drawstart', async () => {
          setSelectedGeofence(undefined);
          name = await setFreshFenceName(
            selectedLayer?.id,
            EntityType.Geofence,
            freshFencesRef.current.map(f => f.name),
          );
        });
        editRef.current.draw.on('drawend', e => {
          if (foundLayer.getSource().get('name') === TRACKING_BOUNDS) e.feature.setStyle(undefined);
          drawEnd(
            olmap,
            e.feature.getGeometry(),
            foundLayer.getSource(),
            GeofenceEntityType.Polygon,
          );
          e.feature.set('name', name);
          e.feature.set('type', FenceEntityType.Polygon);
          e.feature.set('zone', FenceZone.none);
          e.feature.set('geomobyProperties', {});
          e.feature.set('geomobyOverrides', []);
        });
        break;
      case DrawType.Multipolygon:
        editRef.current.draw = interactionStyle(
          Interactions.Draw,
          foundLayer.getSource(),
          GeometryType.MULTI_POLYGON,
          CLEARED_ZONE,
        ) as Draw;
        editRef.current.modify = interactionStyle(
          Interactions.Modify,
          foundLayer.getSource(),
          GeometryType.MULTI_POLYGON,
          CLEARED_ZONE,
        ) as Modify;
        removeInteractions(olmap, [Draw]);
        addDrawInteraction(olmap, editRef.current);

        editRef.current.draw.on('drawstart', async e => {
          // We don't want to be able to deselect the current fence when clicking outside of it, when in Cut mode.
          if (editType !== EditType.Cut) {
            setSelectedGeofence(undefined);
            name = await setFreshFenceName(
              selectedLayer?.id,
              EntityType.Geofence,
              freshFencesRef.current.map(f => f.name),
            );
          }
          e.feature.setStyle(clearedFenceDrawStartStyle);
          if (
            editType === EditType.Delete ||
            (editType === EditType.Cut &&
              e.feature &&
              !isPointInsideAnyMultiPolygon(
                e.feature.getGeometry().getCoordinates()[0][0],
                foundLayer
                  ?.getSource()
                  ?.getFeatures()
                  ?.map(f => f.getGeometry())
                  ?.filter(g => g instanceof MultiPolygon) as MultiPolygon[],
              ))
          ) {
            editRef.current?.draw.abortDrawing();
          }
        });

        foundLayer.setStyle(updateGeofencesStyle(new Map()));
        editRef.current.draw.on('drawend', e => {
          e.feature.setStyle(undefined);
          drawEnd(
            olmap,
            e.feature.getGeometry(),
            foundLayer.getSource(),
            GeofenceEntityType.Multipolygon,
          );
          e.feature.set('name', name);
          e.feature.set('type', FenceEntityType.Multipolygon);
          e.feature.set('zone', FenceZone.cleared);
          e.feature.set('geomobyProperties', {});
          e.feature.set('geomobyOverrides', []);
        });
        break;
      case DrawType.Tripwire:
        editRef.current.draw = new Draw({
          source: foundLayer.getSource(),
          type: GeometryType.LINE_STRING,
          stopClick: true,
          maxPoints: 2,
          minPoints: 2,
        });
        addDrawInteraction(olmap, editRef.current);
        foundLayer.setStyle(updateGeofencesStyle(new Map()));

        editRef.current.draw.on('drawstart', async () => {
          setSelectedGeofence(undefined);
          name = await setFreshFenceName(
            selectedLayer?.id,
            EntityType.Geofence,
            freshFencesRef.current.map(f => f.name),
          );
        });
        editRef.current.draw.on('drawend', e => {
          drawEnd(olmap, e.feature.getGeometry(), foundLayer.getSource(), GeofenceEntityType.Line);
          e.feature.set('name', name);
          e.feature.set('type', FenceEntityType.Line);
          e.feature.set('geomobyProperties', {});
          e.feature.set('geomobyOverrides', []);
          if (!editRef.current) return;
          olmap.removeInteraction(editRef.current.modify);
        });
        break;
      case DrawType.MicrofenceGateway:
        editRef.current.draw = new Draw({
          source: foundLayer.getSource(),
          type: GeometryType.POINT,
          stopClick: true,
        });
        addDrawInteraction(olmap, editRef.current);
        editRef.current.draw.on('drawstart', async () => {
          name = await setFreshFenceName(
            MICROFENCE_LAYER_ID,
            EntityType.Microfence,
            freshFencesRef.current.map(f => f.name),
          );
        });
        editRef.current.draw.on('drawend', async e => {
          e.feature.set('type', MicrofenceEntity.Gateway);
          e.feature.set('assetId', {
            mqttgatewayId: '',
          });
          await microfenceDrawEnd(olmap, e.feature);
        });

        editRef.current.modify = new Modify({
          source: foundLayer.getSource(),
          hitDetection: olmap.getAllLayers().find(l => l.get('id') === MICROFENCE_LAYER_ID) as
            | HitDetectionLayer
            | undefined,
        });
        olmap.addInteraction(editRef.current.modify);
        editRef.current.modify.on('modifyend', (e: ModifyEvent) =>
          featureModified(olmap, e, foundLayer.getSource()),
        );
        break;
      case DrawType.MicrofenceBeacon:
        editRef.current.draw = new Draw({
          source: foundLayer.getSource(),
          type: GeometryType.POINT,
          stopClick: true,
        });
        addDrawInteraction(olmap, editRef.current);
        editRef.current.draw.on('drawend', async e => {
          e.feature.set('type', MicrofenceEntity.Beacon);
          e.feature.set('assetId', {
            uuid: '',
            major: 0,
            minor: 0,
          });
          await microfenceDrawEnd(olmap, e.feature);
        });

        editRef.current.modify = new Modify({
          source: foundLayer.getSource(),
          hitDetection: olmap.getAllLayers().find(l => l.get('id') === MICROFENCE_LAYER_ID) as
            | HitDetectionLayer
            | undefined,
        });
        olmap.addInteraction(editRef.current.modify);
        editRef.current.modify.on('modifyend', (e: ModifyEvent) =>
          featureModified(olmap, e, foundLayer.getSource()),
        );
        break;
      case DrawType.MicrofenceDevice:
        editRef.current.draw = new Draw({
          source: foundLayer.getSource(),
          type: GeometryType.POINT,
          stopClick: true,
        });
        olmap.addInteraction(editRef.current.draw);
        editRef.current.draw.on('drawend', async e => {
          e.feature.set('type', MicrofenceEntity.Device);
          e.feature.set('assetId', {
            deviceId: '',
          });
          await microfenceDrawEnd(olmap, e.feature);
        });
        editRef.current.modify = new Modify({
          source: foundLayer.getSource(),
          hitDetection: olmap.getAllLayers().find(l => l.get('id') === MICROFENCE_LAYER_ID) as
            | HitDetectionLayer
            | undefined,
        });
        olmap.addInteraction(editRef.current.modify);
        editRef.current.modify.on('modifyend', (e: ModifyEvent) =>
          featureModified(olmap, e, foundLayer.getSource()),
        );
        break;
      case DrawType.Measure:
        changeMeasurementType(olmap, MeasurementType.M);
        break;
    }
  };

  const changeMeasurementType = useCallback(
    (olmap: OlMap, type: MeasurementType | undefined) => {
      setMeasurementType(type);
      if (!type) return;

      if (type.includes('2')) {
        updateMeasurementType(olmap, type, 'Polygon');
      } else {
        updateMeasurementType(olmap, type, 'LineString');
      }
    },
    [updateMeasurementType],
  );

  const findBufferFromBreach = useCallback(
    (olmap: OlMap, breachId: string, layerId: string): string | undefined => {
      const layerFromMap = getLayerFromMap(olmap, layerId);
      return [
        ...availableGeofences,
        ...(layerFromMap
          ?.getSource()
          ?.getFeatures()
          ?.map(f => f.getProperties()) ?? []),
      ]?.find(f => f.parentId === breachId)?.id;
    },
    [availableGeofences],
  );

  const updateBufferZoneGeometry = useCallback(
    async (
      olmap: OlMap,
      fence: GridRowData,
      offset: number,
      bufferShape: BufferShapeType,
    ): Promise<{ breach: Feature<Polygon>; buffer: Feature<Polygon> } | undefined> => {
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      const feature = await findFeature(olmap, fence.id, fence.layerId, FenceEntityType.Polygon);
      if (!feature) {
        console.warn('UI should not be able to add zones to a feature not in the selected layer');
        return;
      }
      const geometry = (feature as Feature<Geometry>).getGeometry();
      if (!geometry) {
        console.warn('UI should not be able to add zones to a feature not without a geometry');
        return;
      }
      const coords: number[][] = (geometry as Polygon).getCoordinates()[0];
      if (!coords.length) {
        console.warn('UI should not be able to add zones to a feature without coords');
        return;
      }

      // Find a circle that contains the feature
      const center = getCenter(geometry.getExtent());
      const centerRadius = Math.max(
        ...coords.map(coord => new LineString([center, coord]).getLength()),
      );

      // Using the centroid may result in a smaller circle
      const centroid = coords
        .reduce((acc, cur) => [acc[0] + cur[0], acc[1] + cur[1]])
        .map(sum => sum / coords.length);
      const centroidRadius = Math.max(
        ...coords.map(coord => new LineString([centroid, coord]).getLength()),
      );

      const radiusScaleFactor = 1.005; // Determined by trial and error
      const coordinates = (geometry as Polygon)
        .getCoordinates()[0]
        .map(coord => transform(coord, 'EPSG:3857', 'EPSG:4326'));
      const maxY = Math.max(...coordinates.map(coord => coord[1]));
      const minY = Math.min(...coordinates.map(coord => coord[1]));
      const scaledOffset = offset / Math.cos((((maxY + minY) / 2) * Math.PI) / 180);

      // Use whichever has the smaller radius, adding on the buffer distance (scaling both as per the above)
      if (bufferShape === BufferShapeType.Circle) {
        return {
          breach: feature as Feature<Polygon>,
          buffer: new Feature(
            fromCircle(
              centerRadius < centroidRadius
                ? new Circle(center, centerRadius * radiusScaleFactor + scaledOffset)
                : new Circle(centroid, centroidRadius * radiusScaleFactor + scaledOffset),
            ),
          ),
        };
      } else {
        const bufferZone = (
          await axios.post<{ points: { type: string; coordinates: number[][][] } }>(
            `${triggersUrl}/${cid}/${pid}/geofences/${feature.get('layerId')}/polygon/build-zone`,
            {
              geometry: {
                type: 'Polygon',
                coordinates: [coordinates],
              },
              bufferMetres: offset === undefined || offset < 1 ? 1 : offset,
              limit: 1000,
            },
            authedRequestConfig,
          )
        ).data;

        const bufferPoints = new GeoJSON().readFeature(bufferZone.points, {
          featureProjection: 'EPSG:4326',
        });
        bufferPoints.getGeometry().applyTransform(gpsToMap);
        return {
          breach: feature as Feature<Polygon>,
          buffer: new Feature({
            geometry: new Polygon((bufferPoints?.getGeometry() as Polygon).getCoordinates()),
          }),
        };
      }
    },
    [authedRequestConfig, cid, findFeature, pid, triggersUrl],
  );

  const setAsBreachZone = useCallback(
    async (
      olmap: OlMap,
      fence: GridRowData,
      bufferId: string | undefined,
      offset: number,
      bufferShape: BufferShapeType,
    ): Promise<void> => {
      const layer = getLayerFromMap(olmap, fence.layerId);
      if (!layer) {
        console.warn('UI should not be able to delete a feature nonexistent layer.');
        return;
      }
      const geometries = await updateBufferZoneGeometry(olmap, fence, offset, bufferShape);
      if (!geometries) {
        console.warn('UI was unable to create/update the desired buffer zone.');
        return;
      }

      const breach = geometries.breach;
      const buffer = geometries.buffer;
      if (bufferId) {
        const existingBuffer = await findFeature(
          olmap,
          bufferId,
          fence.layerId,
          FenceEntityType.Polygon,
        );
        if (existingBuffer) {
          const updatedBuffer = buffer;
          updatedBuffer.setProperties({
            ...existingBuffer.getProperties(),
            geometry: updatedBuffer.getGeometry(),
            fresh: existingBuffer.get('id')?.includes(FRESH),
            updated: !existingBuffer.get('id')?.includes(FRESH),
            geometryHasChanged: true,
          });
          layer.getSource().removeFeature(existingBuffer);
          layer.getSource().addFeature(updatedBuffer);
          return;
        }
      }

      const newFenceName = `${breach.get('name')}_warning`.replace(/(\D)0$/, '$1');
      const newfenceId = `fresh-buffer-${new Date().getTime()}`;
      buffer.setProperties({
        name: newFenceName,
        id: newfenceId,
        layerId: breach.get('layerId'),
        type: 'polygon',
        fresh: true,
        zone: FenceZone.buffer,
        geomobyProperties: {},
        geomobyOverrides: [],
        parentId: breach.get('id'),
      });
      layer.getSource().addFeature(buffer);

      breach.setProperties({
        zone: FenceZone.breach,
        parentId: undefined,
        updated: true,
      });

      const breachIsFresh = freshFencesRef.current.find(f => f.id === breach.get('id'));
      if (breachIsFresh) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== breach.get('id')),
          { ...breachIsFresh, zone: FenceZone.breach },
        ];
      }

      if (availableGeofences.length > 0) {
        const index = availableGeofences.findIndex(f => f.id === fence.id);
        const existingBufferIndex = availableGeofences.findIndex(
          f => f.id === breach.get('parentId'),
        );
        if (existingBufferIndex > -1) {
          availableGeofences.splice(existingBufferIndex, 1);
        }
        const bufferZone = {
          name: newFenceName,
          id: newfenceId,
          type: 'polygon',
          zone: FenceZone.buffer,
          geomobyProperties: {},
          geomobyOverrides: [] as GeomobyOverride[],
          parentId: breach.get('id'),
        } as FenceNameIdZone;
        if (index > -1) {
          availableGeofences[index].zone = FenceZone.breach;
          availableGeofences[index].parentId = undefined;
        }
        freshFencesRef.current = [...freshFencesRef.current, bufferZone];
        availableGeofences.splice(index + 1, 0, bufferZone);
      }
      setAvailableGeofences(availableGeofences);
      setFreshFences(freshFencesRef.current);
      setZoneChange(new Date());
    },
    [availableGeofences, findFeature, updateBufferZoneGeometry],
  );

  const setAsBufferZone = useCallback(
    async (olmap: OlMap, fence: GridRowData): Promise<void> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) {
        console.warn('UI should not be able to modify a feature while no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      const feature = await findFeature(olmap, fence.id, selectedLayer.id, FenceEntityType.Polygon);
      if (!feature) {
        console.warn('UI should not be able to add zones to a feature not in the selected layer');
        return;
      }
      const geometry = feature.getGeometry();
      if (!geometry) {
        console.warn('UI should not be able to add zones to a feature without geometry');
        return;
      }

      const containedFeatures = polygonsContainedWithinPolygon(geometry, foundLayer.getSource());
      const containedFeature = containedFeatures.length === 1 ? containedFeatures[0] : undefined;

      if (!containedFeature?.get('parentId')) {
        containedFeature?.setProperties({
          zone: FenceZone.breach,
          type: 'polygon',
          parentId: undefined,
          updated: true,
        });
        feature.setProperties({
          zone: FenceZone.buffer,
          type: 'polygon',
          parentId: containedFeature?.get('id'),
          name: `${containedFeature?.get('name')}_warning`,
          updated: true,
        });
      }

      const bufferIsFresh = freshFencesRef.current.find(f => f.id === feature.get('id'));
      if (bufferIsFresh) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== feature.get('id')),
          {
            ...bufferIsFresh,
            name: `${containedFeature?.get('name')}_warning`,
            zone: FenceZone.buffer,
          },
        ];
      }
      const breachIsFresh = freshFencesRef.current.find(f => f.id === containedFeature?.get('id'));
      if (breachIsFresh) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== containedFeature?.get('id')),
          { ...breachIsFresh, zone: FenceZone.breach },
        ];
      }

      if (selectedLayer?.id !== MICROFENCE_LAYER_ID) {
        if (availableGeofences.length > 0) {
          const bufferIndex = availableGeofences.findIndex(f => f.id === fence.id);
          if (bufferIndex > -1 && containedFeature) {
            const containedFeatureIndex = availableGeofences.findIndex(
              f => f.id === containedFeature.getProperties().id,
            );
            if (containedFeatureIndex > -1) {
              availableGeofences[containedFeatureIndex].zone = FenceZone.breach;
              availableGeofences[containedFeatureIndex].parentId = undefined;
            }
            availableGeofences[bufferIndex].zone = FenceZone.buffer;
            availableGeofences[bufferIndex].parentId = containedFeature.get('id');
            availableGeofences[bufferIndex].name = `${containedFeature.get('name')}_warning`;
          }
        }
        setAvailableGeofences(availableGeofences);
      }
      setSelectedGeofence(feature.getProperties() as GridRowData);
      setZoneChange(new Date());
      setFreshFences(freshFencesRef.current);
    },
    [availableGeofences, findFeature, selectedLayer],
  );

  const unsetAsBreachZone = useCallback(
    async (olmap: OlMap, fence: GridRowData, bufferId: string): Promise<void> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) {
        console.warn('UI should not be able to modify a feature while no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      const feature = await findFeature(olmap, fence.id, selectedLayer.id, FenceEntityType.Polygon);
      if (!feature) {
        console.warn('UI should not be able to add zones to a feature not in the selected layer');
        return;
      }

      const relatedFeature = await findFeature(
        olmap,
        bufferId,
        selectedLayer.id,
        FenceEntityType.Polygon,
      );
      if (relatedFeature) {
        deleteFeature(olmap, relatedFeature, FenceEntityType.Polygon);
      }
      feature.setProperties({
        zone: FenceZone.none,
        parentId: undefined,
        updated: true,
      });

      const breachIsFresh = freshFencesRef.current.find(f => f.id === feature.get('id'));
      if (breachIsFresh) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== feature.get('id')),
          { ...breachIsFresh, zone: FenceZone.none },
        ];
      }

      if (selectedLayer?.id !== MICROFENCE_LAYER_ID) {
        if (availableGeofences.length > 0 && relatedFeature) {
          const relatedFeatureIndex = availableGeofences.findIndex(
            f => f.id === relatedFeature.get('id'),
          );
          if (relatedFeatureIndex > -1) {
            freshFencesRef.current = freshFencesRef.current.filter(
              f => f.id !== relatedFeature?.get('id'),
            );
            setFreshFences(freshFencesRef.current);
            availableGeofences.splice(relatedFeatureIndex, 1);
          }
          const breachIndex = availableGeofences.findIndex(f => f.id === feature.get('id'));
          if (breachIndex > -1) {
            availableGeofences[breachIndex].zone = FenceZone.none;
            availableGeofences[breachIndex].parentId = undefined;
          }
        }
        setAvailableGeofences(availableGeofences);
      }
      setSelectedGeofence(feature.getProperties() as GridRowData);
      setZoneChange(new Date());
    },
    [availableGeofences, deleteFeature, findFeature, selectedLayer],
  );

  const setZone = useCallback(
    async (olmap: OlMap, fence: GridRowData, zone: FenceZone): Promise<void> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) {
        console.warn('UI should not be able to modify a feature while no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      const feature = await findFeature(olmap, fence.id, selectedLayer.id, FenceEntityType.Polygon);
      if (!feature) {
        console.warn('UI should not be able to add zones to a feature not in the selected layer');
        return;
      }

      feature.setProperties({
        ...feature.getProperties(),
        zone: zone,
        updated: true,
      });

      const zoneIsFresh = freshFencesRef.current.find(f => f.id === feature.get('id'));
      if (zoneIsFresh) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== feature.get('id')),
          { ...zoneIsFresh, zone: zone },
        ];
      }

      if (selectedLayer?.id !== MICROFENCE_LAYER_ID) {
        if (availableGeofences.length > 0) {
          const index = availableGeofences.findIndex(f => f.id === fence.id);
          if (index > -1) {
            availableGeofences[index].zone = zone;
            availableGeofences[index].parentId = undefined;
          }
        }
        setAvailableGeofences(availableGeofences);
      }

      setSelectedGeofence(feature.getProperties() as GridRowData);
      setFreshFences(freshFencesRef.current);
    },
    [availableGeofences, findFeature, selectedLayer],
  );

  const unsetZone = useCallback(
    async (olmap: OlMap, fence: GridRowData): Promise<void> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) {
        console.warn('UI should not be able to modify a feature while no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      const feature = await findFeature(olmap, fence.id, selectedLayer.id, FenceEntityType.Polygon);
      if (!feature) {
        console.warn('UI should not be able to add zones to a feature not in the selected layer');
        return;
      }

      feature.setProperties({
        zone: FenceZone.none,
        parentId: undefined,
        updated: true,
      });

      const clearedIsFresh = freshFencesRef.current.find(f => f.id === feature.get('id'));
      if (clearedIsFresh) {
        freshFencesRef.current = [
          ...freshFencesRef.current.filter(f => f.id !== feature.get('id')),
          { ...clearedIsFresh, zone: FenceZone.none },
        ];
      }

      if (selectedLayer?.id !== MICROFENCE_LAYER_ID) {
        if (availableGeofences.length > 0) {
          const clearedIndex = availableGeofences.findIndex(f => f.id === feature.get('id'));
          if (clearedIndex > -1) {
            availableGeofences[clearedIndex].zone = FenceZone.none;
            availableGeofences[clearedIndex].parentId = undefined;
          }
        }
        setAvailableGeofences(availableGeofences);
      }
      setSelectedGeofence(feature.getProperties() as GridRowData);
      setZoneChange(new Date());
      setFreshFences(freshFencesRef.current);
    },
    [availableGeofences, findFeature, selectedLayer],
  );

  const removeBufferZone = useCallback(
    async (olmap: OlMap, fence: GridRowData): Promise<void> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) {
        console.warn('UI should not be able to modify a feature while no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      const feature = await findFeature(olmap, fence.id, selectedLayer.id, FenceEntityType.Polygon);
      if (!feature) {
        console.warn('UI should not be able to add zones to a feature not in the selected layer');
        return;
      }

      const relatedFeature = feature.get('parentId')
        ? foundLayer
            .getSource()
            .getFeatures()
            .find(f => f.get('id') === feature.get('parentId'))
        : undefined;
      relatedFeature?.setProperties({
        zone: FenceZone.none,
        updated: true,
      });

      deleteFeature(olmap, feature, FenceEntityType.Polygon);

      if (selectedLayer?.id !== MICROFENCE_LAYER_ID) {
        if (availableGeofences.length > 0) {
          const relatedFeatureIndex = availableGeofences.findIndex(
            f => f.id === relatedFeature?.get('id') || f.id === feature.get('parentId'),
          );
          if (relatedFeatureIndex > -1) {
            availableGeofences[relatedFeatureIndex].zone = FenceZone.none;
            availableGeofences[relatedFeatureIndex].parentId = undefined;
          }

          const bufferIndex = availableGeofences.findIndex(f => f.id === fence.id);
          if (bufferIndex > -1) {
            availableGeofences.splice(bufferIndex, 1);
          }
        }
        setAvailableGeofences(availableGeofences);
      }
      setSelectedGeofence(feature.getProperties() as GridRowData);
      setZoneChange(new Date());
    },
    [availableGeofences, deleteFeature, findFeature, selectedLayer],
  );

  const updateGeomobyOverrides = useCallback(
    async (
      olmap: OlMap,
      geomobyOverrides: GeomobyOverride[],
    ): Promise<GeomobyOverride[] | undefined> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) return;
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      if (!selectedGeofence) {
        console.warn('UI should not be able to add geomobyOverrides while no feature is selected');
        return;
      }

      const feature = foundLayer
        .getSource()
        .getFeatures()
        .find(f => f.get('id') === selectedGeofence?.id);
      let foundFeature;
      if (!feature) {
        const fenceType = geometryTypeOfEntity(selectedGeofence);
        foundFeature = await findFeature(
          olmap,
          selectedGeofence?.id,
          selectedGeofence?.layerId,
          fenceType,
        );
        if (foundFeature) {
          foundFeature.setProperties({
            ...foundFeature.getProperties(),
            geomobyOverrides,
            updated: true,
          });
        }
      }

      if (!feature && !foundFeature) {
        console.warn(
          'UI should not be able to add geomobyOverrides to a feature not in the selected layer',
        );
        return;
      }

      if (feature) {
        feature.setProperties({
          geomobyOverrides,
          updated: true,
        });
      }

      setLayersHaveChanged(true);
      return geomobyOverrides;
    },
    [findFeature, selectedGeofence, selectedLayer],
  );

  const updateGeomobyProperties = useCallback(
    async (
      olmap: OlMap,
      geomobyProperties: Record<string, string>,
    ): Promise<Record<string, string> | undefined> => {
      const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
      if (!selectedLayer || !foundLayer) {
        console.warn('UI should not be able to modify a feature while no layer selected');
        return;
      }
      if (!olmap) {
        console.warn('UI should not be able to modify a feature while no map exists');
        return;
      }
      if (!selectedGeofence && !selectedMicrofence) {
        console.warn('UI should not be able to add geomobyProperties while no feature is selected');
        return;
      }

      const feature = foundLayer
        .getSource()
        .getFeatures()
        .find(f => f.get('id') === (selectedGeofence?.id ?? selectedMicrofence?.id));
      let foundFeature;
      if (!feature) {
        const fenceType = selectedGeofence ? geometryTypeOfEntity(selectedGeofence) : undefined;
        foundFeature = await findFeature(
          olmap,
          selectedGeofence?.id,
          selectedGeofence?.layerId,
          fenceType,
        );
        if (foundFeature) {
          foundFeature.setProperties({
            ...foundFeature.getProperties(),
            geomobyProperties,
            updated: true,
          });
        }
      }

      if (!feature && !foundFeature) {
        console.warn(
          'UI should not be able to add geomobyProperties to a feature not in the selected layer',
        );
        return;
      }

      if (feature) {
        feature.setProperties({
          geomobyProperties,
          updated: true,
        });
      }

      setLayersHaveChanged(true);
      return geomobyProperties;
    },
    [findFeature, selectedGeofence, selectedLayer, selectedMicrofence],
  );

  const moveUnknownFenceToExistingLayer = useCallback(
    (olmap: OlMap, layerId: string) => {
      const freshUnknownLayer = getLayerFromMap(olmap, undefined, UNKNOWN_LAYER);
      const existingLayer = getLayerFromMap(olmap, layerIds.find(lyr => lyr.id === layerId)?.id);
      if (freshUnknownLayer && existingLayer && olmap) {
        const freshUnknownFence = freshUnknownLayer.getSource().getFeatures()?.[0];
        freshUnknownFence.set('layerId', existingLayer.getSource().get('id'));
        freshUnknownFence.set('layerName', existingLayer.getSource().get('name'));
        existingLayer.getSource().addFeature(freshUnknownFence);
        olmap.removeLayer(freshUnknownLayer);
        setLayerIds(layerIds.filter(lyr => lyr.id !== UNKNOWN_LAYER));
      }
    },
    [layerIds],
  );

  const initialise = useCallback(async () => {
    if (!specifiedCoordinates || mapState?.map) return false;
    const { map: olmap, setSource: setMapSource } = createMap(
      MapType.EDIT_MAP,
      createMapDefaults({
        sourceType: mapSourceType,
        edit: true,
        specifiedCoordinates: specifiedCoordinates ?? [initialLongitude, initialLatitude],
        mapApiKeys,
      }),
    );
    setSourceRef.current = setMapSource;

    setFencesLoading(true);
    const [vectorLayers, microfenceLayer] = await Promise.all([
      getVectorLayers(triggersUrl, authedRequestConfig, cid, pid, bounds),
      getMicrofencesVectorLayer(triggersUrl, authedRequestConfig, cid, pid),
    ]);

    setLayerIds([
      {
        id: MICROFENCE_LAYER_ID,
        name: MICROFENCE_LAYER_LABEL,
      },
      ...(vectorLayers?.map(vectorLayer => {
        return {
          id: vectorLayer.id,
          name: vectorLayer.name,
        };
      }) ?? []),
    ]);
    vectorLayers?.forEach(vectorLayer => {
      vectorLayer.source.setSource(vectorLayer.source.getSource());
      olmap.addLayer(vectorLayer.source);
    });
    olmap.addLayer(microfenceLayer);

    setFencesLoading(false);
    setMapIsLoading(false);
    if (!olmap || olmap.hasListener('click')) return;
    setMapClickDeselection(olmap);
    setMapMoveHandler(olmap);
    setCurrentCenter(olmap.getView().getCenter());

    // LTP-1236 Editor map re-factoring Phase 3 may involve putting crucial values in the one object, as is done with Live/Replay
    setMapState({
      map: olmap,
    });

    // There maybe more than just the size of the map that is incomplete, but the size is definetely holding up the map
    // when the interval is too short. Hence, the 2000ms interval vs a 10ms interval.
    return olmap.getSize() !== undefined;
  }, [
    specifiedCoordinates,
    mapState?.map,
    authedRequestConfig,
    bounds,
    cid,
    mapApiKeys,
    mapSourceType,
    pid,
    setMapClickDeselection,
    setMapMoveHandler,
    triggersUrl,
  ]);

  // Initialise map
  useEffect(() => {
    if (initRef.current) return;
    let timerId: NodeJS.Timeout | null = null;

    const tryInitialise = async (delay: number) => {
      timerId = setTimeout(async () => {
        const hasCompleteMap = await initialise();
        if (!hasCompleteMap && delay < 10000) {
          tryInitialise(delay + 2000);
        } else {
          initRef.current = true;
        }
      }, delay);
    };

    tryInitialise(2000);
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [initialise]);

  // Load More Fences
  const updateMap = useCallback(
    async (olmap: OlMap, newBounds: Bounds) => {
      if (!olmap || editMode) return;

      const loadMoreFences = async () => {
        setFencesLoading(true);
        const { latitude, longitude, extentInDegrees } = newBounds;
        const [xMax, yMax] = transform(
          [longitude + extentInDegrees, latitude + extentInDegrees / 2],
          'EPSG:4326',
          'EPSG:3857',
        );
        const [xMin, yMin] = transform(
          [longitude - extentInDegrees, latitude - extentInDegrees / 2],
          'EPSG:4326',
          'EPSG:3857',
        );
        const extent: [number, number, number, number] = [xMax, yMax, xMin, yMin];
        // already got features for this extent
        if (knownExtentsRef.current.some(ext => containsExtent(ext, extent))) {
          setFencesLoading(false);
          return;
        }

        getVectorLayers(triggersUrl, authedRequestConfig, cid, pid, newBounds)
          .then(vectorLayers => {
            setFencesLoading(false);
            vectorLayers?.forEach(vectorLayer => {
              const existingLayer = getLayerFromMap(olmap, undefined, vectorLayer.name);
              const newFeatures = vectorLayer.source.getSource().getFeatures();
              const existingSource = existingLayer?.getSource();
              const existingFeatures = existingSource?.getFeatures() || [];
              const deletedFeatures: [string, boolean][] = deletedFenceIdsRef.current.map(
                ({ id }) => [id, true],
              );
              const keepIds = Object.fromEntries([
                ...existingFeatures.map(f => [`${f.get('id')}`, true]),
                ...deletedFeatures,
              ]);
              const keepFeatures = newFeatures.filter(f => !keepIds[f.get('id')]);
              existingSource?.addFeatures(keepFeatures);
            });
            setFencesLoading(false);
          })
          .catch(error => {
            setFencesLoading(false);
            const errorMessage = normaliseErrorMessage(error as AxiosError, EntityType.Microfence);
            setSaveNotification({
              id: SaveResult.FAIL,
              action: '',
              message: errorMessage,
            });
          });
        knownExtentsRef.current = [...knownExtentsRef.current, extent];
      };
      loadMoreFences();
      if (
        selectedGeofence &&
        selectedLayer?.id !== MICROFENCE_LAYER_ID &&
        geometryTypeOfEntity(selectedGeofence) !== FenceEntityType.Line
      ) {
        const geometry = selectedGeofence?.geometry;
        const foundLayer = getLayerFromMap(olmap, selectedLayer?.id);
        if (geometry && foundLayer) {
          setHasFences(
            polygonsContainedWithinPolygon(geometry, foundLayer.getSource()).length === 1,
          );
        }
      }
    },
    [
      authedRequestConfig,
      cid,
      editMode,
      pid,
      selectedGeofence,
      selectedLayer?.id,
      setSaveNotification,
      triggersUrl,
    ],
  );

  // Handle selected layer change
  useEffect(() => {
    if (!mapState?.map) return;
    if (selectedLayer?.name === UNKNOWN_LAYER) return;
    if (selectedLayer) {
      if (editMode) {
        stopEditingFeature(mapState.map);
      }
      layerIds.forEach(layer =>
        changeVisibility(
          mapState.map,
          layer.id,
          layer.id === selectedLayer?.id || showGhostGeofences,
        ),
      );
    } else {
      setAvailableGeofences([]);
      setSelectedGeofence(undefined);
      setSelectedMicrofence(undefined);
      deselectFences(mapState?.map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayer, mapState?.map]);

  useEffect(() => {
    // Ideally it would be better do this everytime fenceIds changes but we can't always capture that event.
    if (!mapState?.map) return;
    if (editRef.current) {
      const e = editRef.current;
      mapState.map.removeInteraction(e.modify);
      mapState.map.removeInteraction(e.modifyScaleRot);
      mapState.map.removeInteraction(e.translate);
    }

    if (selectedLayer && drawType) {
      const foundLayer = getLayerFromMap(mapState.map, selectedLayer.id);
      if (!foundLayer) return;
      const modifiedInteration =
        drawType === DrawType.Tripwire
          ? GeofenceEntityType.Line
          : drawType === DrawType.Circle || drawType === DrawType.Polygon
          ? GeofenceEntityType.Polygon
          : drawType === DrawType.Multipolygon
          ? GeofenceEntityType.Multipolygon
          : undefined;
      if (modifiedInteration && foundLayer) {
        addShapeChangeModifyInteration(mapState.map, foundLayer.getSource(), modifiedInteration);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneChange, mapState?.map]);

  useEffect(() => {
    if (!mapState?.map) return;
    changeDrawShape(mapState.map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawType, editType, mapState?.map]);

  // Display purple location marker
  useEffect(() => {
    if (!mapState) return;
    if (!locationSearchData) {
      mapState.map?.setLayers(
        mapState.map?.getAllLayers().filter(layer => layer?.getClassName() !== PIN_LAYER),
      );
      return;
    }

    const { coords, isStreetAddress } = locationSearchData;
    animateToLocation(
      mapState.map?.getView(),
      [...coords, ...coords] as [number, number, number, number],
      () => {
        mapState.map?.getView()?.setZoom(isStreetAddress ? STREET_ADDRESS_ZOOM_LEVEL : 14);
      },
    );
    dropLocationMarker(mapState.map, new Feature(new Point(coords)));
  }, [locationSearchData, mapState]);

  // Get a true count of the features in the current layer.
  useEffect(() => {
    if (geofenceFilter !== undefined) return;
    totalFenceCountRef.current = count;
  }, [count, geofenceFilter]);

  // Disable draw and modify when loading fences.
  useEffect(() => {
    if (!mapState) return;
    mapState.map
      .getInteractions()
      .getArray()
      .filter((i: Interaction) => i instanceof Modify || i instanceof Draw)
      .forEach(interaction => {
        interaction.setActive(!isLoading);
      });
  }, [mapState, isLoading]);

  // Update visibility of geofences. Most importantly everytime new geofences are loaded to the map.
  useEffect(() => {
    if (
      !mapState?.map ||
      !layerIds ||
      selectedLayer?.id === MICROFENCE_LAYER_ID ||
      selectedLayer?.id === UNKNOWN_LAYER ||
      showFilter ||
      fencesLoading ||
      locationSearchData
    )
      return;
    layerIds.forEach(layer =>
      changeVisibility(
        mapState?.map,
        layer.id,
        !selectedLayer?.id || selectedLayer?.id === ALL_LAYERS
          ? true
          : selectedLayer?.id === layer.id,
      ),
    );
    mapState?.map.getView().adjustZoom(-1);
    mapState?.map.getView().adjustZoom(+1);
  }, [
    geofenceFilter,
    layerIds,
    mapState?.map,
    selectedLayer?.id,
    changeVisibility,
    showFilter,
    fencesLoading,
    locationSearchData,
  ]);

  // So 'layersHaveChanged' can be used in event handlers.
  useEffect(() => {
    layersHaveChangedRef.current = layersHaveChanged;
  }, [layersHaveChanged]);

  // So 'editType' can be used in event handlers.
  useEffect(() => {
    editTypeRef.current = editType;
  }, [editType]);

  useEffect(() => {
    setMicrofences(
      mapState?.map
        ?.getAllLayers()
        ?.find(layer => layer instanceof VectorLayer && layer.get('id') === MICROFENCE_LAYER_ID)
        ?.getSource()
        ?.getFeatures()
        .map((fence: Feature<Geometry>) => fence.getProperties()),
    );
  }, [mapState]);

  const value = useMemo(
    () => ({
      animateToFeature,
      availableGeofences,
      availableMicrofences,
      changeMeasurementType,
      changeVisibility,
      clearFilter,
      count,
      createNewLayer,
      currentCenter,
      deleteFeature,
      deleteLayer,
      deletedFenceIdsRef,
      deselectFence,
      drawType,
      editFeature,
      editMode,
      editType,
      fencesLoading,
      findBufferFromBreach,
      findFeature,
      freshFences,
      geofenceFilter,
      hasFences,
      isLoading,
      knownExtentsRef,
      layerIds,
      layersHaveChanged,
      locationDisplay,
      mapIsLoading,
      mapSourceType,
      mapState,
      measurementType,
      microfenceFilter,
      microfences,
      moveUnknownFenceToExistingLayer,
      openDialog,
      openGenericDialog,
      reassignedFences,
      refreshSearch,
      removeBufferZone,
      resetLayerChanges,
      saveLayerChanges,
      searchType,
      selectedFromMap,
      selectedGeofence,
      selectedLayer,
      selectedMicrofence,
      setAsBreachZone,
      setAsBufferZone,
      setAvailableGeofences,
      setAvailableMicrofences,
      setClearFilter,
      setCount,
      setDeselectFence,
      setDrawType,
      setEditMode,
      setEditType,
      setFreshFences,
      setGeofenceFilter,
      setIsLoading,
      setLayerIds,
      setLayersHaveChanged,
      setLocationDisplay,
      setLocationSearchData,
      setMapSourceType,
      setMicrofenceFilter,
      setOpenDialog,
      setOpenGenericDialog,
      setReassignedFences,
      setRefreshSearch,
      setRenamingLayer,
      setSearchType,
      setSelectedFromMap,
      setSelectedGeofence,
      setSelectedLayer,
      setSelectedMicrofence,
      setSourceRef,
      setShowFilter,
      setShowGhostGeofences,
      setZone,
      showFilter,
      showGhostGeofences,
      stopEditingFeature,
      unsetAsBreachZone,
      unsetZone,
      updateFenceIdentifiers,
      updateGeomobyOverrides,
      updateGeomobyProperties,
    }),
    [
      animateToFeature,
      availableGeofences,
      availableMicrofences,
      changeMeasurementType,
      changeVisibility,
      clearFilter,
      count,
      createNewLayer,
      currentCenter,
      deleteFeature,
      deleteLayer,
      deletedFenceIdsRef,
      deselectFence,
      drawType,
      editFeature,
      editMode,
      editType,
      fencesLoading,
      findBufferFromBreach,
      findFeature,
      freshFences,
      geofenceFilter,
      hasFences,
      isLoading,
      knownExtentsRef,
      layerIds,
      layersHaveChanged,
      locationDisplay,
      mapIsLoading,
      mapSourceType,
      mapState,
      measurementType,
      microfenceFilter,
      microfences,
      moveUnknownFenceToExistingLayer,
      openDialog,
      openGenericDialog,
      reassignedFences,
      refreshSearch,
      removeBufferZone,
      resetLayerChanges,
      saveLayerChanges,
      searchType,
      selectedFromMap,
      selectedGeofence,
      selectedLayer,
      selectedMicrofence,
      setAsBreachZone,
      setAsBufferZone,
      setAvailableGeofences,
      setAvailableMicrofences,
      setClearFilter,
      setCount,
      setDeselectFence,
      setDrawType,
      setEditMode,
      setEditType,
      setFreshFences,
      setGeofenceFilter,
      setIsLoading,
      setLayerIds,
      setLayersHaveChanged,
      setLocationDisplay,
      setLocationSearchData,
      setMapSourceType,
      setMicrofenceFilter,
      setOpenDialog,
      setOpenGenericDialog,
      setReassignedFences,
      setRefreshSearch,
      setRenamingLayer,
      setSearchType,
      setSelectedFromMap,
      setSelectedGeofence,
      setSelectedLayer,
      setSelectedMicrofence,
      setSourceRef,
      setShowFilter,
      setShowGhostGeofences,
      setZone,
      showFilter,
      showGhostGeofences,
      stopEditingFeature,
      unsetAsBreachZone,
      unsetZone,
      updateFenceIdentifiers,
      updateGeomobyOverrides,
      updateGeomobyProperties,
    ],
  );
  return <EditorMapContext.Provider value={value}>{children}</EditorMapContext.Provider>;
};

export const useEditorMap = () => useContext(EditorMapContext);
